import {
	boolean,
	createBuilder,
	createSchema,
	definePermissions,
	enumeration,
	json,
	NOBODY_CAN,
	number,
	relationships,
	string,
	table,
} from "@rocicorp/zero"

import type {
	AgentRecommendationPayload,
	AgentRecommendationStatus,
	AgentRecommendationType,
} from "@productlane/lib/src/agent-recommendations"
import type { AiDraftSource } from "@productlane/lib/src/ai"
import type { ToolPolicies, ZeroJwtPayload } from "@productlane/lib/src/browser"
import type { ContactFormField } from "@productlane/lib/src/contact-form-fields"
import type { StoredSidebarLayout } from "@productlane/lib/src/sidebar-layout"

import type { OfficeHours } from "./types"
import type {
	ExpressionBuilder,
	PermissionsConfig,
	Row,
	Transaction,
} from "@rocicorp/zero"

// Define tables using the new chainable syntax
const user = table("users")
	.from("User")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		email: string(),
		role: string(),
		imageUrl: string().optional(),
		name: string().optional(),
		feedbackEmailsEnabled: boolean(),
		changelogEmailsEnabled: boolean(),
		upvoteEmailsEnabled: boolean(),
		docsProposalEmailsEnabled: boolean(),
		// @deprecated Removed from the notification settings UI; no longer user-configurable.
		unpublishedProjectsEmailsEnabled: boolean(),
		inboxZeroEmailsEnabled: boolean(),
		selectedTeamIds: json<string[]>(),
		newThreadNotificationsEnabled: boolean(),
		aiHandoffNotificationsEnabled: boolean(),
		assignedThreadsReplyReceivedNotificationsEnabled: boolean(),
		unassignedThreadsReplyReceivedNotificationsEnabled: boolean(),
		commentMentionsNotificationsEnabled: boolean(),
		assignmentChangesNotificationsEnabled: boolean(),
		replyAllByDefault: boolean(),
		onboarded: boolean(),
		version: number(),
		source: string().optional(),
	})
	.primaryKey("id")

const workspace = table("workspaces")
	.from("Workspace")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		name: string(),
		domain: string(),
		logoUrl: string().optional(),
		selectedTeamIds: json<string[]>(),
		linearSyncTeamIds: json<string[]>(),
		maxResponseTimeHours: number().optional(),
		slaEnabled: boolean(),
		slaUseBusinessHours: boolean(),
		timezone: string().optional(),
		officeHours: json<OfficeHours[]>().optional(),
		onboardingCompanySize: string().optional(),
		onboardingRole: string().optional(),
		onboardingReferralSource: string().optional(),
		onboardingSignupReason: string().optional(),
		isEmailIntegrationEnabled: boolean(),
		emailIntegrationAddress: string().optional(),
		hasBeenSetup: boolean(),
		showOnboardingBanner: boolean(),
		helpCenterTitle: string(),
		helpCenterSummary: string(),
		hasTriedImportAfterSignup: boolean(),
		autoCreateLinearIssues: boolean(),
		autoAssignFromLinearOwner: boolean(),
		autoAssignAgentToBugs: boolean(),
		excludeManuallyCreatedIssues: boolean(),
		postLinearResourceComment: boolean(),
		linkSuggestionsEnabled: boolean(),
		autoProposeDocsUpdates: boolean(),
		docsCustomPrompt: string().optional(),
		docsProposalCadence: enumeration<"ON_MERGE" | "DAILY" | "WEEKLY">(),
		docsAutoMergeEnabled: boolean(),
		docsProposalGithubTrigger: enumeration<
			"OFF" | "PR_MERGED" | "COMMIT_TO_MAIN"
		>(),
		domainCompanyAssignmentEnabled: boolean(),
		autoSwitchContactToLatestSender: boolean(),
		linearOrganizationId: string().optional(),
		linearTokenRevoked: boolean(),
		remainingGeneratedThreadTitles: number(),
		aiResolutionsUsedThisMonth: number(),
		aiPayPerUseEnabled: boolean(),
		aiResolutionMonthlyLimit: number().optional(),
		domainPublic: boolean(),
		googleDomain: string().optional(),
		isGoogleAnalyticsEnabled: boolean(),
		googleTagId: string().optional(),
		isPosthogEnabled: boolean(),
		usePortalLogoUrl: boolean(),
		portalLogoUrl: string().optional(),
		portalSwitcherVisible: boolean(),
		portalLandingSection: string().optional(),
		changelogPublic: boolean(),
		isGoogleAuthRequired: boolean(),
		isDueDateEnabled: boolean(),
		isRoadmapsEnabled: boolean(),
		weeklyRoadmapEmailEnabled: boolean(),
		userId: string().optional(),
		changelogAutomaticDrafts: boolean(),
		changelogEmailNotifications: boolean(),
		changelogCustomPrompt: string().optional(),
		changelogLink1Description: string().optional(),
		changelogLink1Url: string().optional(),
		changelogLink2Description: string().optional(),
		changelogLink2Url: string().optional(),
		helpCenterPublic: boolean(),
		helpCenterShowAiActions: boolean(),
		helpCenterNavStyle: enumeration<"BAR" | "DROPDOWN" | "NAVBAR">(),
		defaultLinearTeamId: string().optional(),
		revenueFormatting: enumeration<"MONTHLY" | "ANNUAL">(),
		revenueCurrency: string(),
		stripeCustomerId: string().optional(),
		stripeSubscriptionId: string().optional(),
		stripePriceId: string().optional(),
		stripeCurrentPeriodEnd: number(),
		darkModeAccentColor: string(),
		accentColor: string(),
		hideBranding: boolean(),
		customDomain: string().optional(),
		darkMode: boolean().optional(),
		defaultLanguage: string().optional(),
		enabledLanguages: json<string[]>().optional(),
		buttonTextColor: string(),
		darkModeButtonTextColor: string(),
		portalEmbedImageUrl: string().optional(),
		portalBackgroundColor: string().optional(),
		portalDarkModeBackgroundColor: string().optional(),
		remainingAiChangelogGenerations: number(),
		subscribeUrl: string().optional(),
	})
	.primaryKey("id")

const membership = table("memberships")
	.from("Membership")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		role: string(),
		userId: string(),
		workspaceId: string(),
		emailSignature: string().optional(),
		sidebarLayout: json<StoredSidebarLayout>().optional(),
		outOfOfficeUntil: number().optional(),
		outOfOfficeCoverId: string().optional(),
	})
	.primaryKey("id")

const feedback = table("feedbacks")
	.from("Feedback")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		isRead: boolean(),
		lastMessageAt: number(),
		lastInboundMessageAt: number().optional(),
		lastOutboundMessageAt: number().optional(),
		lastStateChangeAt: number().optional(),
		text: string(),
		painLevel: enumeration<"HIGH" | "MEDIUM" | "LOW" | "UNKNOWN">(),
		state: enumeration<
			"NEW" | "PROCESSED" | "COMPLETED" | "SNOOZED" | "UNSNOOZED"
		>(),
		origin: enumeration<
			| "INAPP"
			| "PORTAL"
			| "SUPPORT_PORTAL"
			| "API"
			| "SLACK"
			| "SLACK_CONNECT"
			| "INTERCOM"
			| "INTERCOM_ATTACHMENT"
			| "ZENDESK_ATTACHMENT"
			| "ZENDESK"
			| "FRONT_ATTACHMENT"
			| "EMAIL"
			| "ZAPIER"
			| "HUBSPOT"
			| "PLAIN"
			| "CALENDAR"
			| "WIDGET"
			| "AI_CHAT"
			| "UPVOTE"
			| "LIVE_CHAT"
			| "TEAMS"
			| "LINEAR"
			| "CUSTOM"
		>(),
		customChannel: string().optional(),
		title: string().optional(),
		number: number().optional(),
		chatThreadId: string().optional(),
		snoozedUntil: number().optional(),
		workspaceId: string(),
		contactIsUnverified: boolean().optional(),
		hasLinkedIssue: boolean().optional(),
		contactId: string().optional(),
		companyId: string().optional(),
		reporterId: string().optional(),
		assigneeId: string().optional(),
		attachments: json<string[]>().optional(),
		recordingId: string().optional(),
		videoId: string().optional(),
		slackChannelId: string().optional(),
		linearAttachmentId: string().optional(),
		intercomId: string().optional(),
		frontId: string().optional(),
		zendeskId: string().optional(),
		hubspotId: string().optional(),
		plainId: string().optional(),
		productboardId: string().optional(),
		slackReplyId: string().optional(),
		uniqueId: string().optional(),
		replied: boolean().optional(),
		slaDeadlineAt: number().optional(),
		aiDraftHtml: string().optional(),
		aiDraftGeneratedAt: number().optional(),
		aiDraftStartedAt: number().optional(),
		aiDraftErrorAt: number().optional(),
		aiDraftSources: json<AiDraftSource[]>().optional(),
		agentEscalationReviewedAt: number().optional(),
		suggestionsCheckedAt: number().optional(),
		emailAddressId: string().optional(),
		// Null means the main portal, or an origin that has no portal at all.
		portalInstanceId: string().optional(),
	})
	.primaryKey("id")

const company = table("companies")
	.from("Company")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		linearCustomerId: string().optional(),
		size: number().optional(),
		revenue: number().optional(),
		statusId: string().optional(),
		statusName: string().optional(),
		statusColor: string().optional(),
		tierId: string().optional(),
		tierName: string().optional(),
		linearOwnerId: string().optional(),
		linearOwnerName: string().optional(),
		linearOwnerAvatarUrl: string().optional(),
		logoUrl: string().optional(),
		domains: json<string[]>(),
		name: string(),
		domain: string(),
		autoAdd: boolean().optional(),
		workspaceId: string(),
		intercomId: string().optional(),
		hubspotId: string().optional(),
		productboardId: string().optional(),
		attioId: string().optional(),
	})
	.primaryKey("id")

const contact = table("contacts")
	.from("Contact")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		email: string(),
		name: string().optional(),
		imageUrl: string().optional(),
		workspaceId: string(),
		intercomId: string().optional(),
		hubspotId: string().optional(),
		productboardId: string().optional(),
		attioId: string().optional(),
		zendeskId: string().optional(),
		companyId: string().optional(),
		isSubscribed: boolean(),
		isEmailSuppressed: boolean(),
	})
	.primaryKey("id")

const tagGroup = table("tagGroups")
	.from("TagGroup")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		color: string(),
		name: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const tag = table("tags")
	.from("Tag")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		color: string(),
		icon: string(),
		name: string(),
		tagGroupId: string(),
		workspaceId: string(),
		productboardId: string().optional(),
		summaryString: string().optional(),
		summaryUpdated: number().optional(),
	})
	.primaryKey("id")

const video = table("videos")
	.from("Video")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		uploadComplete: boolean(),
		videoDuration: number().optional(),
		videoUrl: string().optional(),
		workspaceId: string(),
		muxAssetId: string().optional(),
		muxPlaybackId: string().optional(),
		fileType: string(),
		contentType: string(),
		uploaderId: string().optional(),
		recordingId: string().optional(),
		transcriptStatus: string().optional(),
		transcriptError: string().optional(),
		transcriptId: string().optional(),
	})
	.primaryKey("id")

const meetingRecording = table("meetingRecordings")
	.from("MeetingRecording")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		status: string(),
		recordingStartedAt: number().optional(),
		hasRecorded: boolean(),
		fatalError: string().optional(),
		botId: string(),
		calendarEventId: string().optional(),
		meetingUrl: string(),
		userId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const transcriptUtterance = table("transcriptUtterances")
	.from("TranscriptUtterance")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		videoId: string(),
		contactId: string().optional(),
		startTime: number(),
		endTime: number(),
		speaker: string(),
		text: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const project = table("projects")
	.from("Project")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		completedAt: number().optional(),
		name: string(),
		description: string().optional(),
		publicDescription: string().optional(),
		publicTitle: string().optional(),
		notes: string().optional(),
		isVisible: boolean().optional(),
		icon: string().optional(),
		startDate: number().optional(),
		startDateResolution: string().optional(),
		targetDate: number().optional(),
		targetDateResolution: string().optional(),
		state: string().optional(),
		stateName: string().optional(),
		stateColor: string().optional(),
		statusId: string().optional(),
		leadId: string().optional(),
		health: string().optional(),
		priority: string().optional(),
		sortOrder: number(),
		portalSortOrder: number().optional(),
		archived: boolean(),
		betaUrl: string().optional(),
		color: string().optional(),
		progress: number().optional(),
		linearTeamIds: json<string[]>(),
		linearTeamNames: json<string[]>(),
		url: string().optional(),
		workspaceId: string(),
		linearProjectId: string().optional(),
		summaryString: string(),
		summaryUpdated: number().optional(),
		totalScore: number(),
		portalInstanceIds: json<string[]>().optional(),
	})
	.primaryKey("id")

const issue = table("issues")
	.from("Issue")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		dueDate: number().optional(),
		workspaceId: string(),
		completedAt: number().optional(),
		linearTicketId: string(),
		title: string(),
		description: string().optional(),
		descriptionData: json().optional(),
		publicDescription: string().optional(),
		publicTitle: string().optional(),
		team: string(),
		identifier: string(),
		status: string(),
		statusColor: string().optional(),
		statusName: string().optional(),
		statusId: string().optional(),
		assigneeId: string().optional(),
		delegateId: string().optional(),
		cycleId: string().optional(),
		parentLinearId: string().optional(),
		previousIdentifiers: json<string[]>().optional(),
		priority: string(),
		url: string(),
		projectId: string().optional(),
		sortOrder: number(),
		portalSortOrder: number().optional(),
		summaryString: string(),
		summaryUpdated: number().optional(),
		isVisible: boolean().optional(),
		totalScore: number(),
		portalInstanceIds: json<string[]>().optional(),
	})
	.primaryKey("id")

const customerNeed = table("customerNeeds")
	.from("CustomerNeed")
	.columns({
		id: string(),
		feedbackId: string(),
		issueId: string().optional(),
		projectId: string().optional(),
		linearCustomerNeedId: string(),
		priority: number(),
	})
	.primaryKey("id")

const linearLabel = table("linearLabels")
	.from("LinearLabel")
	.columns({
		id: string(),
		linearLabelId: string(),
		isDeleted: boolean(),
		name: string(),
		color: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const linearCycle = table("linearCycles")
	.from("LinearCycle")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		linearCycleId: string(),
		teamId: string(),
		number: number(),
		name: string().optional(),
		startsAt: number(),
		endsAt: number(),
		completedAt: number().optional(),
		workspaceId: string(),
	})
	.primaryKey("id")

const issueLabel = table("issueLabels")
	.from("IssueLabel")
	.columns({
		issueId: string(),
		labelId: string(),
		workspaceId: string(),
	})
	.primaryKey("issueId", "labelId")

const codingAgentRun = table("codingAgentRuns")
	.from("CodingAgentRun")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		workspaceId: string(),
		issueId: string().optional(),
		triggeredById: string().optional(),
		triggerSource: string(),
		provider: string(),
		providerRunId: string().optional(),
		status: enumeration<
			"PENDING" | "CREATING" | "RUNNING" | "FINISHED" | "FAILED" | "STOPPED"
		>(),
		statusChangedAt: number(),
		prUrl: string().optional(),
		branchName: string().optional(),
		summary: string().optional(),
		artifacts: json().optional(),
		errorMessage: string().optional(),
		repository: string(),
		prompt: string().optional(),
		feedbackId: string().optional(),
		prNumber: number().optional(),
		reviewState: enumeration<"PENDING" | "APPROVED" | "REJECTED">().optional(),
	})
	.primaryKey("id")

const issueTranslation = table("issueTranslations")
	.from("IssueTranslation")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		title: string().optional(),
		description: string().optional(),
		language: string(),
		issueId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const projectTranslation = table("projectTranslations")
	.from("ProjectTranslation")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		name: string().optional(),
		description: string().optional(),
		summary: string().optional(),
		language: string(),
		projectId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const changelogTranslation = table("changelogTranslations")
	.from("ChangelogTranslation")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		title: string(),
		notes: json().optional(),
		imageUrl: string().optional(),
		imageBlurhash: string().optional(),
		coverVideoUrl: string().optional(),
		language: string(),
		translationStatus: enumeration<
			"QUEUED" | "TRANSLATING" | "COMPLETED" | "FAILED"
		>().optional(),
		changelogId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const helpCenterArticleTranslation = table("helpCenterArticleTranslations")
	.from("HelpCenterArticleTranslation")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		title: string(),
		content: json<string>(),
		summary: string().optional(),
		imageUrl: string().optional(),
		language: string(),
		translationStatus: enumeration<
			"QUEUED" | "TRANSLATING" | "COMPLETED" | "FAILED"
		>().optional(),
		helpCenterArticleId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const helpCenterGroupTranslation = table("helpCenterGroupTranslations")
	.from("HelpCenterGroupTranslation")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		title: string().optional(),
		description: string().optional(),
		language: string(),
		helpCenterGroupId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const helpCenterHomeTranslation = table("helpCenterHomeTranslations")
	.from("HelpCenterHomeTranslation")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		title: string().optional(),
		summary: string().optional(),
		language: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const aiChatSettingsTranslation = table("aiChatSettingsTranslations")
	.from("AiChatSettingsTranslation")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		welcomeMessage: string().optional(),
		exampleQuestions: json<string[]>(),
		language: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const changelog = table("changelogs")
	.from("Changelog")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		date: number().optional(),
		published: boolean(),
		title: string(),
		slug: string().optional(),
		notes: json(),
		archived: boolean(),
		imageUrl: string().optional(),
		imageBlurhash: string().optional(),
		coverVideoUrl: string().optional(),
		projectId: string().optional(),
		workspaceId: string(),
		portalInstanceId: string().optional(),
		portalInstanceIds: json<string[]>().optional(),
		changelogStatus: enumeration<
			| "QUEUED"
			| "FETCHING_PROJECTS"
			| "FETCHING_ISSUES"
			| "GENERATING"
			| "UPDATING"
			| "COMPLETED"
			| "FAILED"
		>().optional(),
	})
	.primaryKey("id")

const changelogTag = table("changelogTags")
	.from("ChangelogTag")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		color: string(),
		icon: string().optional(),
		name: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const portalInstance = table("portalInstances")
	.from("PortalInstance")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		slug: string(),
		name: string(),
		isDefault: boolean(),
		customDomain: string().optional(),
		showInSelector: boolean(),
		logoUrl: string().optional(),
		defaultLinearTeamId: string().optional(),
		defaultEmailAddressId: string().optional(),
		showDocs: boolean(),
		showChangelog: boolean(),
		showRoadmap: boolean(),
		showSupport: boolean(),
		landingSection: string().optional(),
		overridesAuth: boolean(),
		isGoogleAuthRequired: boolean(),
		googleDomain: string().optional(),
		overridesCustomLinks: boolean(),
		usePortalLogoUrl: boolean(),
		portalLogoUrl: string().optional(),
		changelogLink1Description: string().optional(),
		changelogLink1Url: string().optional(),
		changelogLink2Description: string().optional(),
		changelogLink2Url: string().optional(),
		overridesTheme: boolean(),
		darkMode: boolean().optional(),
		accentColor: string().optional(),
		accentTextColor: string().optional(),
		buttonTextColor: string().optional(),
		darkModeAccentColor: string().optional(),
		darkModeAccentTextColor: string().optional(),
		darkModeButtonTextColor: string().optional(),
		backgroundColor: string().optional(),
		darkModeBackgroundColor: string().optional(),
		portalEmbedImageUrl: string().optional(),
		workspaceId: string(),
	})
	.primaryKey("id")

const changelogToChangelogTag = table("changelogToChangelogTags")
	.from("_ChangelogToChangelogTag")
	.columns({
		A: string(), // Changelog
		B: string(), // ChangelogTag
	})
	.primaryKey("A", "B")

const helpCenterGroup = table("helpCenterGroups")
	.from("HelpCenterGroup")
	.columns({
		id: string(),
		name: string(),
		order: number(),
		workspaceId: string(),
		version: number(),
		portalInstanceId: string().optional(),
		tabId: string().optional(),
	})
	.primaryKey("id")

// apiSpec stays out: sync state and diffs are read over tRPC by the API reference page.
const helpCenterTab = table("helpCenterTabs")
	.from("HelpCenterTab")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		name: string(),
		icon: string(),
		order: number(),
		kind: enumeration<"DOCS" | "API_REFERENCE">(),
		slug: string().optional(),
		translations: json<Record<string, string>>(),
		isPublished: boolean(),
		workspaceId: string(),
		portalInstanceId: string().optional(),
	})
	.primaryKey("id")

const helpCenterArticle = table("helpCenterArticles")
	.from("HelpCenterArticle")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		order: number(),
		date: number().optional(),
		published: boolean(),
		visibility: enumeration<"PUBLIC" | "UNLISTED" | "INTERNAL">(),
		kind: enumeration<"DOC" | "LINK">(),
		externalUrl: string().optional(),
		title: string(),
		icon: string(),
		content: json(),
		summary: string().optional(),
		archived: boolean(),
		urlName: string(),
		// Old paths that 308 to urlName. The server owns it, so inserts leave it out.
		previousUrlNames: json<string[]>().optional(),
		imageUrl: string().optional(),
		imageBlurhash: string().optional(),
		workspaceId: string(),
		helpCenterGroupId: string().optional(),
		parentArticleId: string().optional(),
		showOnHomePage: boolean(),
		homeSection: enumeration<"GET_STARTED" | "POPULAR">().optional(),
		homeOrder: number().optional(),
		customHomeSectionId: string().optional(),
		portalInstanceId: string().optional(),
	})
	.primaryKey("id")

const helpCenterCustomHomeSection = table("helpCenterCustomHomeSections")
	.from("HelpCenterCustomHomeSection")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		name: string().optional(),
		order: number(),
		articleLimit: number(),
		workspaceId: string(),
		portalInstanceId: string().optional(),
		groupId: string().optional(),
	})
	.primaryKey("id")

const changelogVersion = table("changelogVersions")
	.from("ChangelogVersion")
	.columns({
		id: string(),
		createdAt: number(),
		version: number(),
		title: string(),
		notes: json(),
		imageUrl: string().optional(),
		coverVideoUrl: string().optional(),
		changelogId: string(),
		language: string(),
		createdBySource: enumeration<"USER" | "AI" | "API">(),
		createdById: string().optional(),
	})
	.primaryKey("id")

const helpCenterArticleVersion = table("helpCenterArticleVersions")
	.from("HelpCenterArticleVersion")
	.columns({
		id: string(),
		createdAt: number(),
		version: number(),
		title: string(),
		content: json(),
		imageUrl: string().optional(),
		imageBlurhash: string().optional(),
		summary: string().optional(),
		icon: string(),
		articleId: string(),
		language: string(),
		appliedSteps: json().optional(),
		parentVersionId: string().optional(),
		createdBySource: enumeration<"USER" | "AI" | "API">(),
		createdById: string().optional(),
	})
	.primaryKey("id")

const helpCenterArticleProposal = table("helpCenterArticleProposals")
	.from("HelpCenterArticleProposal")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		articleId: string().optional(),
		baseVersionId: string().optional(),
		kind: enumeration<"EDIT" | "CREATE" | "DELETE">(),
		proposalGroupId: string().optional(),
		proposedSteps: json(),
		proposedTitle: string().optional(),
		proposedGroupId: string().optional(),
		proposedIcon: string().optional(),
		source: enumeration<
			"MANUAL" | "CHANGELOG" | "ISSUE_COMPLETED" | "THREAD"
		>(),
		authorType: enumeration<"USER" | "AGENT">(),
		createdByUserId: string().optional(),
		status: enumeration<
			"DRAFT" | "OPEN" | "ACCEPTED" | "REJECTED" | "SUPERSEDED"
		>(),
		closedByUserId: string().optional(),
		closedAt: number().optional(),
		appliedVersionId: string().optional(),
		agentReview: string().optional(),
		workspaceId: string(),
	})
	.primaryKey("id")

const helpCenterArticleProposalTranslation = table(
	"helpCenterArticleProposalTranslations",
)
	.from("HelpCenterArticleProposalTranslation")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		language: string(),
		title: string().optional(),
		content: string().optional(),
		translationStatus: enumeration<
			"QUEUED" | "TRANSLATING" | "COMPLETED" | "FAILED"
		>(),
		failureReason: string().optional(),
		proposalId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const helpCenterProposalGroup = table("helpCenterProposalGroups")
	.from("HelpCenterProposalGroup")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		title: string().optional(),
		// Agent-written 1-3 sentence rationale set via `set_description`.
		// Rendered under the headline on the proposal review card.
		description: string().optional(),
		source: enumeration<
			"MANUAL" | "CHANGELOG" | "ISSUE_COMPLETED" | "THREAD"
		>(),
		authorType: enumeration<"USER" | "AGENT">(),
		createdByUserId: string().optional(),
		// `owner/repo#number` identifiers for the PRs the agent run was
		// triggered with. Stored as a Postgres TEXT[]; surfaced to Zero as a
		// JSON array.
		pullRequestIdentifiers: json(),
		status: enumeration<
			"DRAFT" | "OPEN" | "ACCEPTED" | "REJECTED" | "SUPERSEDED"
		>(),
		closedByUserId: string().optional(),
		closedAt: number().optional(),
		workspaceId: string(),
	})
	.primaryKey("id")

// Implicit Prisma M2M join table for HelpCenterProposalGroup ↔ Issue.
// Mirrors the `_FeedbackToIssue` shape; Prisma names the table by sorting
// the two model names alphabetically.
const helpCenterProposalGroupToIssue = table("helpCenterProposalGroupToIssues")
	.from("_HelpCenterProposalGroupToIssue")
	.columns({
		A: string(), // HelpCenterProposalGroup
		B: string(), // Issue
	})
	.primaryKey("A", "B")

// Implicit Prisma M2M join table for Feedback (thread) ↔ HelpCenterProposalGroup.
// Records which customer conversations a docs-proposal group was derived from.
const helpCenterProposalGroupToThread = table(
	"helpCenterProposalGroupToThreads",
)
	.from("_FeedbackToHelpCenterProposalGroup")
	.columns({
		A: string(), // Feedback
		B: string(), // HelpCenterProposalGroup
	})
	.primaryKey("A", "B")

const agentRecommendation = table("agentRecommendations")
	.from("AgentRecommendation")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		workspaceId: string(),
		type: enumeration<AgentRecommendationType>(),
		status: enumeration<AgentRecommendationStatus>(),
		title: string(),
		summary: string(),
		impactPerMonth: number(),
		conversationCount: number(),
		rerunAnswered: number().optional(),
		rerunTotal: number().optional(),
		payload: json<AgentRecommendationPayload>(),
		proposalGroupId: string().optional(),
		dismissReason: string().optional(),
		acceptedAt: number().optional(),
		closedByUserId: string().optional(),
	})
	.primaryKey("id")

// Internal only: suggestions name Linear issues and projects, so no customer surface reads this table.
const suggestion = table("suggestions")
	.from("Suggestion")
	.columns({
		id: string(),
		createdAt: number(),
		workspaceId: string(),
		kind: string(),
		targetId: string(),
		score: number(),
		reason: string(),
		quote: string().optional(),
		source: string(),
		meta: json<{ linkedThreadCount?: number }>().optional(),
		acceptedAt: number().optional(),
		dismissedAt: number().optional(),
		threadId: string().optional(),
		issueId: string().optional(),
		projectId: string().optional(),
	})
	.primaryKey("id")

const widgetSettings = table("widgetSettings")
	.from("WidgetSettings")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isEnabled: boolean(),
		workspaceId: string(),
		widgetKey: string(),
		hasCustomLinksEnabled: boolean(),
		customLinksHeading: string().optional(),
		isChangelogEnabled: boolean(),
		isRoadmapEnabled: boolean(),
		isSupportEnabled: boolean(),
		isBrandingEnabled: boolean(),
		hasCustomStylingEnabled: boolean(),
		isFeedbackEnabled: boolean(),
		isWhatsNewEnabled: boolean(),
		isWidgetIconVisible: boolean(),
		widgetIconType: enumeration<"NONE" | "QUESTION_MARK" | "CHAT">(),
		widgetSize: enumeration<"SMALL" | "MEDIUM" | "LARGE">(),
		defaultView: string(),
		isHelpCenterEnabled: boolean(),
		isLiveChatEnabled: boolean(),
		isGuestChatEnabled: boolean(),
		isAiFirstEnabled: boolean(),
		identityVerificationEnabled: boolean(),
		isPortalAuthEnabled: boolean(),
		signingSecretCreatedAt: number().optional(),
		isContactAvatarsEnabled: boolean(),
		contactFormFields: json<ContactFormField[]>(),
	})
	.primaryKey("id")

const widgetContactAvatar = table("widgetContactAvatars")
	.from("WidgetContactAvatar")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		order: number(),
		overrideImageUrl: string().optional(),
		widgetSettingsId: string(),
		membershipId: string(),
	})
	.primaryKey("id")

const aiChatSettings = table("aiChatSettings")
	.from("AiChatSettings")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		workspaceId: string(),
		isEnabled: boolean(),
		createLinearIssues: boolean(),
		welcomeMessage: string(),
		exampleQuestions: json<string[]>(),
		customInstructions: string().optional(),
		aiAutoRepliesEnabled: boolean(),
		deploySlack: boolean(),
		deployTeams: boolean(),
		deployApi: boolean(),
		deploySupportPortal: boolean(),
		deployFeatureRequests: boolean(),
		aiDraftsEnabled: boolean(),
		aiDraftsTrigger: enumeration<
			"NEW_THREADS_ONLY" | "EVERY_INBOUND_MESSAGE"
		>(),
		aiDraftsCloseTheLoopEnabled: boolean(),
		aiDraftsCustomPrompt: string().optional(),
		routinesEnabled: boolean(),
		routinesPrompt: string().optional(),
		shadowRepliesEnabled: boolean(),
		closeLoopAutoSend: boolean(),
		closeLoopTemplate: string().optional(),
		closeLoopUpvoteTemplate: string().optional(),
		closeLoopIssueStatusIds: json<string[]>(),
		closeLoopProjectTemplate: string().optional(),
		closeLoopProjectStatusIds: json<string[]>(),
		bugInspectEnabled: boolean(),
	})
	.primaryKey("id")

const widgetLink = table("widgetLinks")
	.from("WidgetLink")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		title: string().optional(),
		url: string().optional(),
		icon: string().optional(),
		isEnabled: boolean(),
		workspaceId: string(),
	})
	.primaryKey("id")

const customView = table("customViews")
	.from("CustomView")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		name: string(),
		slug: string(),
		icon: string().optional(),
		filters: json<{
			roadmap?: {
				negations?: {
					statuses?: boolean
					projectIds?: boolean
					labelIds?: boolean
				}
				statuses?: string[]
				teamIds?: string[]
				projectIds?: Array<string | null>
				priorities?: string[]
				companyIds?: Array<string | null>
				sources?: Array<"customers" | "internal">
				labelIds?: string[]
			}
			negations?: {
				origins?: boolean
				creatorIds?: boolean
				painLevels?: boolean
				assigneeIds?: boolean
				projectIds?: boolean
				tagIds?: boolean
				issueIds?: boolean
				companyIds?: boolean
				personIds?: boolean
				statuses?: boolean
				replyStates?: boolean
				companyTiers?: boolean
				companyStatuses?: boolean
				companyOwnerIds?: boolean
				emailAddressIds?: boolean
				portalInstanceIds?: boolean
				slackWorkspaceIds?: boolean
				slackChannelIds?: boolean
			}
			emailAddressIds?: Array<string | null>
			portalInstanceIds?: Array<string | null>
			slackWorkspaceIds?: Array<string>
			slackChannelIds?: Array<string>
			companyIds?: Array<string | null>
			personIds?: Array<string | null>
			issueIds?: Array<string | null>
			tagIds?: Array<string | null>
			projectIds?: Array<string | null>
			assigneeIds?: Array<string | null>
			origins?: Array<
				| "API"
				| "INAPP"
				| "PORTAL"
				| "SUPPORT_PORTAL"
				| "EMAIL"
				| "SLACK"
				| "SLACK_CONNECT"
				| "INTERCOM"
				| "INTERCOM_ATTACHMENT"
				| "ZENDESK"
				| "ZENDESK_ATTACHMENT"
				| "FRONT_ATTACHMENT"
				| "ZAPIER"
				| "HUBSPOT"
				| "PLAIN"
				| "CALENDAR"
				| "WIDGET"
				| "AI_CHAT"
				| "UPVOTE"
				| "LIVE_CHAT"
				| "TEAMS"
				| "LINEAR"
				| "CUSTOM"
				| null
			>
			painLevels?: Array<"HIGH" | "MEDIUM" | "LOW" | "UNKNOWN">
			creatorIds?: string[]
			statuses?: Array<
				"NEW" | "PROCESSED" | "COMPLETED" | "SNOOZED" | "UNSNOOZED"
			>
			replyStates?: Array<"NEEDS_RESPONSE" | "REPLIED">
			companyTiers?: string[]
			companyStatuses?: string[]
			companyOwnerIds?: Array<string | null>
			issueStatuses?: string[]
			companyRevenue?: {
				operator: ">=" | "<=" | "=" | "!="
				value: number
			} | null
			companySize?: { operator: ">=" | "<=" | "=" | "!="; value: number } | null
		}>(),
		sortOrder: number(),
		surface: string<"threads" | "roadmap">(),
		workspaceId: string(),
	})
	.primaryKey("id")

const sidebarSection = table("sidebarSections")
	.from("SidebarSection")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		title: string(),
		url: string(),
		logoUrl: string().optional(),
		description: string().optional(),
		order: number(),
		type: enumeration<"HTTP" | "MCP">(),
		availableToAgent: boolean(),
		reachableWithoutIdentity: boolean(),
		allowedMcpTools: json<string[]>(),
		blockedInternalMcpTools: json<string[]>(),
		toolPolicies: json<ToolPolicies>().optional(),
		oauthTokenUrl: string().optional(),
		oauthClientId: string().optional(),
		oauthScope: string().optional(),
		oauthAudience: string().optional(),
		workspaceId: string(),
	})
	.primaryKey("id")

// The header value is a credential: encrypted at rest and never synced to
// clients. Reads and writes of it go through the server mutator only.
const sidebarSectionHeader = table("sidebarSectionHeaders")
	.from("SidebarSectionHeader")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		key: string(),
		sidebarSectionId: string(),
	})
	.primaryKey("id")

const workspaceInvite = table("workspaceInvites")
	.from("WorkspaceInvite")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		acceptedAt: number().optional(),
		workspaceId: string(),
		state: string(),
		inviteName: string(),
		inviteEmail: string(),
		invitedById: string(),
		role: string(),
	})
	.primaryKey("id")

const emailSettings = table("emailSettings")
	.from("EmailSettings")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		primaryEmail: string(),
		domain: string(),
		brandedInboundEmailAddress: string(),
		isEnabled: boolean(),
		isForwardingSetup: boolean(),
		isPrimary: boolean(),
		isBroadcastDefault: boolean(),
		senderName: string().optional(),
		postmarkDomainId: string(),
		txtDnsKey: string(),
		txtDnsValue: string(),
		isTxtDnsActive: boolean(),
		cnameDnsKey: string(),
		cnameDnsValue: string(),
		isCnameDnsActive: boolean(),
		autoReplyEnabled: boolean(),
		autoReplyMessage: string(),
		aiAgentEnabled: boolean(),
		autoReplySenderName: string().optional(),
		forwardedEmailHandling: enumeration<
			"AUTO" | "ORIGINAL_SENDER" | "FORWARDER"
		>(),
		workspaceId: string(),
	})
	.primaryKey("id")

const blockedSender = table("blockedSender")
	.from("BlockedSender")
	.columns({
		id: string(),
		createdAt: number(),
		workspaceId: string(),
		value: string(),
		type: enumeration<"EMAIL" | "DOMAIN">(),
	})
	.primaryKey("id")

const forwardedEmailIgnoreSender = table("forwardedEmailIgnoreSender")
	.from("ForwardedEmailIgnoreSender")
	.columns({
		id: string(),
		createdAt: number(),
		workspaceId: string(),
		email: string(),
	})
	.primaryKey("id")

const personalSlackNotificationSettings = table(
	"personalSlackNotificationSettings",
)
	.from("PersonalSlackNotificationSettings")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		teamId: string(),
		teamName: string(),
		slackUserId: string(),
		followUpMessageNotificationsEnabled: boolean(),
		assignmentChangesNotificationsEnabled: boolean(),
		commentCreatedNotificationsEnabled: boolean(),
		newThreadNotificationsEnabled: boolean(),
		aiHandoffNotificationsEnabled: boolean(),
		commentMentionsNotificationsEnabled: boolean(),
		unassignedThreadReplyNotificationsEnabled: boolean(),
		newEmailCreatedNotificationsEnabled: boolean(),
		docsProposalNotificationsEnabled: boolean(),
		slaAtRiskNotificationsEnabled: boolean(),
		slaBreachedNotificationsEnabled: boolean(),
		membershipId: string(),
	})
	.primaryKey("id")

const slackSettings = table("slackSettings")
	.from("SlackSettings")
	.columns({
		botUserId: string(),
		teamId: string().optional(),
		feedbackHubChannelId: string().optional(),
		feedbackHubBotMessages: boolean().optional(),
		notificationChannelId: string().optional(),
		pushBotReply: boolean().optional(),
		pushBotReplyChannel: boolean(),
		workspaceId: string(),
	})
	.primaryKey("workspaceId")

const slackConnectSettings = table("slackConnectSettings")
	.from("SlackConnectSettings")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		type: string(),
		teamId: string(),
		teamUrl: string(),
		botUserId: string(),
		feedbackHubChannelId: string().optional(),
		feedbackHubBotMessages: boolean().optional(),
		notificationChannelId: string().optional(),
		importantNotificationChannelId: string().optional(),
		pushBotReply: boolean().optional(),
		pushBotReplyChannel: boolean(),
		mutedInboxChannelIds: json<string[]>(),
		groupingWindowMinutes: number(),
		channelWelcomeEnabled: boolean(),
		channelWelcomeMessage: string().optional(),
		communityWelcomeEnabled: boolean(),
		communityWelcomeMessage: string().optional(),
		newChannelsInbox: boolean(),
		linearAsksMode: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const slackChannels = table("slackChannels")
	.from("SlackChannel")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		channelId: string(),
		name: string(),
		isPrivate: boolean(),
		isShared: boolean(),
		isMember: boolean(),
		inbox: boolean(),
		changelog: boolean(),
		type: string().optional(),
		groupingWindowMinutes: number().optional(),
		tagId: string().optional(),
		assigneeId: string().optional(),
		slackConnectSettingsId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const ssoSettings = table("ssoSettings")
	.from("SsoSettings")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		workspaceId: string().optional(),
		portalInstanceId: string().optional(),
		tokenEndpoint: string(),
		isEnabled: boolean(),
		isPortalPrivate: boolean(),
		isDocsPrivate: boolean(),
		isRoadmapPrivate: boolean(),
		isChangelogPrivate: boolean(),
		isRequestsPrivate: boolean(),
	})
	.primaryKey("id")

const intercomSettings = table("intercomSettings")
	.from("IntercomSettings")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		intercomWorkspaceId: string(),
		dataHostRegion: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const hubspotSettings = table("hubspotSettings")
	.from("HubspotSettings")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		hubspotWorkspaceId: string(),
		contactPropertyNames: json<string[]>(),
		companyPropertyNames: json<string[]>(),
		workspaceId: string(),
	})
	.primaryKey("id")

const plainSettings = table("plainSettings")
	.from("PlainSettings")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		webhookId: string(),
		cardId: string(),
		plainWorkspaceId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const teamsSettings = table("teamsSettings")
	.from("TeamsSettings")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		tenantId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const githubSettings = table("githubSettings")
	.from("GithubSettings")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		installationId: string(),
		accountLogin: string(),
		accountType: string(),
		repositoryFullName: string().optional(),
		repositoryId: string().optional(),
		defaultBranch: string().optional(),
		additionalRepositories: json<string[]>(),
		installedByUserId: string().optional(),
		workspaceId: string(),
	})
	.primaryKey("id")

const threadMessage = table("threadMessages")
	.from("ThreadMessage")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		title: string(),
		text: string(),
		actorName: string(),
		actorImage: string().optional(),
		workspaceId: string(),
		threadId: string(),
	})
	.primaryKey("id")

const threadActivity = table("threadActivities")
	.from("ThreadActivity")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		eventType: string(),
		payload: json(),
		workspaceId: string(),
		threadId: string(),
		actorId: string().optional(),
	})
	.primaryKey("id")

const notification = table("notifications")
	.from("Notification")
	.columns({
		id: string(),
		createdAt: number(),
		type: string(),
		reason: string(),
		entityType: string(),
		entityId: string(),
		payload: json(),
		readAt: number().optional(),
		archivedAt: number().optional(),
		workspaceId: string(),
		userId: string(),
		actorId: string().optional(),
		threadId: string().optional(),
	})
	.primaryKey("id")

const notificationSubscription = table("notificationSubscriptions")
	.from("NotificationSubscription")
	.columns({
		id: string(),
		entityType: string(),
		entityId: string(),
		isSubscribed: boolean(),
		source: string(),
		workspaceId: string(),
		userId: string(),
	})
	.primaryKey("id")

const notificationPreference = table("notificationPreferences")
	.from("NotificationPreference")
	.columns({
		id: string(),
		settings: json(),
		workspaceId: string(),
		userId: string(),
	})
	.primaryKey("id")

const email = table("emails")
	.from("Email")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		date: number(),
		version: number(),
		isDeleted: boolean(),
		type: string(),
		source: enumeration<"HUMAN" | "AI" | "AUTO_REPLY" | "API">().optional(),
		subject: string(),
		originalRecipient: string(),
		// Bodies live in emailContents and sync only with a thread's detail.
		preview: string(),
		postmarkMessageId: string().optional(),
		messageId: string().optional(),
		uniqueId: string().optional(),
		threadId: string(),
		workspaceId: string(),
		userId: string().optional(),
		cc: json().optional(),
		bcc: json().optional(),
		to: json().optional(),
		from: json().optional(),
		attachments: json().optional(),
	})
	.primaryKey("id")

const emailContent = table("emailContents")
	.from("EmailContent")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		htmlContent: string(),
		textContent: string(),
		attachments: json().optional(),
		emailId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const attachment = table("attachments")
	.from("Attachment")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		url: string(),
		name: string().optional(),
		originalName: string().optional(),
		fileType: string().optional(),
		contentType: string().optional(),
		size: number().optional(),
		emailId: string().optional(),
		workspaceId: string(),
		uploaderId: string().optional(),
	})
	.primaryKey("id")

const slackUser = table("slackUsers")
	.from("SlackUser")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		slackId: string(),
		username: string().optional(),
		name: string().optional(),
		email: string().optional(),
		imageUrl: string().optional(),
		raw: json(),
		workspaceId: string(),
	})
	.primaryKey("id")

const slackMessage = table("slackMessages")
	.from("SlackMessage")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		type: string(),
		teamId: string(),
		channelId: string(),
		slackId: string(),
		slackUserId: string(),
		slackThreadId: string().optional(),
		text: string(),
		slackLink: string(),
		html: string(),
		workspaceId: string(),
		threadId: string(),
		userId: string().optional(),
		attachments: json().optional(),
	})
	.primaryKey("id")

/** Emoji reactions on thread messages and internal comments; messageType names the table messageId points at. */
const messageReaction = table("messageReactions")
	.from("MessageReaction")
	.columns({
		id: string(),
		createdAt: number(),
		messageType: enumeration<"slack" | "comment">(),
		messageId: string(),
		emoji: string(),
		imageUrl: string().optional(),
		source: enumeration<"SLACK" | "APP" | "LINEAR">(),
		reactorKey: string(),
		reactorName: string().optional(),
		workspaceId: string(),
		threadId: string(),
		userId: string().optional(),
	})
	.primaryKey("id")

const chatMessage = table("chatMessages")
	.from("ChatMessage")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		type: string(),
		platform: string(),
		platformMessageId: string(),
		chatThreadId: string(),
		authorId: string().optional(),
		authorName: string().optional(),
		text: string(),
		html: string(),
		attachments: json().optional(),
		workspaceId: string(),
		threadId: string(),
		userId: string().optional(),
	})
	.primaryKey("id")

const snippet = table("snippets")
	.from("Snippet")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		title: string(),
		html: string(),
		emailId: string().optional(),
		folderId: string().optional(),
		workspaceId: string(),
	})
	.primaryKey("id")

const snippetFolder = table("snippetFolders")
	.from("SnippetFolder")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		name: string(),
		icon: string().optional(),
		workspaceId: string(),
	})
	.primaryKey("id")

const slaPolicy = table("slaPolicies")
	.from("SlaPolicy")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isDeleted: boolean(),
		name: string(),
		conditions: json().optional(),
		position: number(),
		firstResponseMinutes: number().optional(),
		nextResponseMinutes: number().optional(),
		timeToCloseMinutes: number().optional(),
		workspaceId: string(),
	})
	.primaryKey("id")

// Public API v2; keys are listed via Zero (instant in the settings UI),
// but creation/revocation go through tRPC so the server controls secret
// generation and never round-trips the plaintext through the sync layer.
// Scopes column is opaque to permissions; access is controlled by
// workspace membership.
const apiKey = table("apiKeys")
	.from("ApiKey")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		workspaceId: string(),
		createdById: string(),
		label: string(),
		prefix: string(),
		keyPreview: string(),
		scopes: json<string[]>(),
		lastUsedAt: number().optional(),
		revokedAt: number().optional(),
		expiresAt: number().optional(),
	})
	.primaryKey("id")

const webhook = table("webhooks")
	.from("Webhook")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		workspaceId: string(),
		// Optional because we backfilled the column on existing rows
		// where the original creator is unknown.
		createdById: string().optional(),
		label: string(),
		url: string(),
		events: json<string[]>(),
		active: boolean(),
	})
	.primaryKey("id")

// Append-only delivery audit log. Synced read-only to clients (the
// settings page renders the recent activity for a webhook). Capped to
// 200 rows in the query layer (queries.ts `webhookDeliveries`) to bound
// bandwidth on chatty workspaces. Mutations (create/update/delete) happen
// exclusively on the server in the dispatch / deliver / retry Inngest functions.
const webhookDelivery = table("webhookDeliveries")
	.from("WebhookDelivery")
	.columns({
		id: string(),
		createdAt: number(),
		webhookId: string(),
		eventId: string(),
		eventType: string(),
		payload: json(),
		attemptCount: number(),
		status: string(),
		responseStatus: number().optional(),
		responseBody: string().optional(),
		responseHeaders: json().optional(),
		durationMs: number().optional(),
		scheduledAt: number(),
		deliveredAt: number().optional(),
		nextAttemptAt: number().optional(),
	})
	.primaryKey("id")

// OAuth 2.1 registered clients. Synced read-only so a future "Connected
// apps" settings screen can list authorized apps instantly. Registration
// (DCR) and consent run server-side via the /oauth endpoints; the secret /
// registration-token hashes never round-trip through a client mutation. The
// token/code tables (access, refresh, authorization codes) are deliberately
// NOT synced - they are ephemeral server-only credential flows.
const oauthClient = table("oauthClients")
	.from("OAuthClient")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		clientName: string(),
		redirectUris: json<string[]>(),
		grantTypes: json<string[]>(),
		scopes: json<string[]>(),
		tokenEndpointAuthMethod: string(),
		registrationAccessTokenHash: string().optional(),
		logoUri: string().optional(),
		clientUri: string().optional(),
		isFirstParty: boolean(),
		isPublished: boolean(),
		ownerUserId: string().optional(),
		ownerWorkspaceId: string().optional(),
	})
	.primaryKey("id")

const allowedEmailDomain = table("allowedEmailDomains")
	.from("AllowedEmailDomain")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		domain: string(),
		verificationEmail: string(),
		hashedVerificationToken: string(),
		verificationEmailSentAt: number().optional(),
		isVerified: boolean(),
		verifiedAt: number().optional(),
		workspaceId: string(),
	})
	.primaryKey("id")

const feedbackTemplate = table("feedbackTemplates")
	.from("FeedbackTemplate")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isStandardEnabled: boolean(),
		isDynamicEnabled: boolean(),
		standardText: string(),
		dynamicText: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const companyMeta = table("companyMetas")
	.from("CompanyMeta")
	.columns({
		domain: string(),
		createdAt: number(),
		updatedAt: number(),
		brandfetchData: json().optional(),
	})
	.primaryKey("domain")

const segment = table("segments")
	.from("Segment")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		name: string(),
		color: string(),
		type: string(),
		intercomId: string().optional(),
		hubspotId: string().optional(),
		workspaceId: string(),
	})
	.primaryKey("id")

const upvote = table("upvotes")
	.from("Upvote")
	.columns({
		id: string(),
		projectId: string(),
		contactId: string(),
		createdAt: number(),
		isDeleted: boolean(),
		version: number(),
		workspaceId: string(),
	})
	.primaryKey("id")

const issueUpvote = table("issueUpvotes")
	.from("IssueUpvote")
	.columns({
		id: string(),
		issueId: string(),
		contactId: string(),
		createdAt: number(),
		isDeleted: boolean(),
		version: number(),
		workspaceId: string(),
	})
	.primaryKey("id")

const roadmapSettings = table("roadmapSettings")
	.from("RoadmapSettings")
	.columns({
		workspaceId: string(),
		sortOrder: string(),
		linearIssueDescription: boolean(),
		googleAuth: boolean(),
		googleDomain: string().optional(),
		groupOrder: json<string[]>(),
		groupIssuesUnderProject: boolean(),
	})
	.primaryKey("workspaceId")

const customerPortalSettings = table("customerPortalSettings")
	.from("CustomerPortalSettings")
	.columns({
		workspaceId: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isEnabled: boolean(),
		teamRequests: boolean(),
		callRecordings: boolean(),
		linearIssueTitles: boolean(),
		linearIssueDescription: boolean(),
		adjustImportance: boolean(),
		showInWidget: boolean(),
		allSourcesEnabled: boolean(),
		portalCompanyScope: enumeration<"STRICT" | "ALLOW_UNTAGGED" | "OFF">(),
	})
	.primaryKey("workspaceId")

const linearRoadmap = table("linearRoadmaps")
	.from("LinearRoadmap")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		name: string(),
		linearRoadmapId: string(),
		sortPosition: number(),
		workspaceId: string(),
	})
	.primaryKey("id")

const comment = table("comments")
	.from("Comment")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		isResolved: boolean(),
		version: number(),
		content: string(),
		attachments: json<string[]>(),
		feedbackId: string(),
		workspaceId: string(),
		userId: string(),
		parentId: string().optional(),
		linearCommentId: string().optional(),
		linearAuthorId: string().optional(),
	})
	.primaryKey("id")

const linearUser = table("linearUsers")
	.from("LinearUser")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		workspaceId: string(),
		linearUserId: string(),
		name: string().optional(),
		email: string().optional(),
		avatarUrl: string().optional(),
		active: boolean(),
		isApp: boolean(),
	})
	.primaryKey("id")

// Only the outcome columns are replicated; transcripts stay server-side. The
// inbox Agent tabs read isResolved / escalatedAt off these rows.
const aiChatSession = table("aiChatSessions")
	.from("AiChatSession")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		isDeleted: boolean(),
		version: number(),
		workspaceId: string(),
		threadId: string().optional(),
		isResolved: boolean().optional(),
		resolvedConfidence: number().optional(),
		escalatedAt: number().optional(),
		userId: string().optional(),
		liveChatSessionId: string().optional(),
		portalInstanceId: string().optional(),
		// Assistant chats only: what the Agent sidebar lists them by.
		scopeKind: string().optional(),
		scopeId: string().optional(),
		title: string().optional(),
		lastMessageAt: number().optional(),
		archivedAt: number().optional(),
	})
	.primaryKey("id")

const liveChatSession = table("liveChatSessions")
	.from("LiveChatSession")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		version: number(),
		isFinished: boolean(),
		finishedAt: number().optional(),
		workspaceId: string(),
		threadId: string(),
	})
	.primaryKey("id")

const liveChatMessage = table("liveChatMessages")
	.from("LiveChatMessage")
	.columns({
		id: string(),
		createdAt: number(),
		updatedAt: number(),
		readAt: number().optional(),
		emailSentAt: number().optional(),
		version: number(),
		isDeleted: boolean(),
		content: string(),
		role: enumeration<"AI" | "USER" | "AGENT" | "SYSTEM">(),
		email: string().optional(),
		name: string().optional(),
		imageUrl: string().optional(),
		sessionId: string(),
		attachments: json<string[]>().optional(),
		emailMessageId: string().optional(),
		// Set only on the agent's own answers; close-loop and bug follow-ups are AI rows too.
		aiChatSessionId: string().optional(),
	})
	.primaryKey("id")

const companyToSegment = table("companyToSegments")
	.from("_CompanyToSegment")
	.columns({
		A: string(), // Company
		B: string(), // Segment
	})
	.primaryKey("A", "B")

const contactCompany = table("contactCompanies")
	.from("ContactCompany")
	.columns({
		id: string(),
		createdAt: number(),
		contactId: string(),
		companyId: string(),
		workspaceId: string(),
	})
	.primaryKey("id")

const contactToSegment = table("contactToSegments")
	.from("_ContactToSegment")
	.columns({
		A: string(), // Contact
		B: string(), // Segment
	})
	.primaryKey("A", "B")

const feedbackToTag = table("feedbackToTags")
	.from("_FeedbackToTag")
	.columns({
		A: string(), // Feedback
		B: string(), // Tag
	})
	.primaryKey("A", "B")

const feedbackToIssue = table("feedbackToIssues")
	.from("_FeedbackToIssue")
	.columns({
		A: string(), // Feedback
		B: string(), // Issue
	})
	.primaryKey("A", "B")

const feedbackToProject = table("feedbackToProjects")
	.from("_FeedbackToProject")
	.columns({
		A: string(), // Feedback
		B: string(), // Project
	})
	.primaryKey("A", "B")

const linearRoadmapToProject = table("linearRoadmapToProjects")
	.from("_LinearRoadmapToProject")
	.columns({
		A: string(), // LinearRoadmap
		B: string(), // Project
	})
	.primaryKey("A", "B")

// Define relationships
const userRelationships = relationships(user, ({ many }) => ({
	memberships: many({
		sourceField: ["id"],
		destField: ["userId"],
		destSchema: membership,
	}),
	reportedFeedbacks: many({
		sourceField: ["id"],
		destField: ["reporterId"],
		destSchema: feedback,
	}),
	assignedFeedbacks: many({
		sourceField: ["id"],
		destField: ["assigneeId"],
		destSchema: feedback,
	}),
	comments: many({
		sourceField: ["id"],
		destField: ["userId"],
		destSchema: comment,
	}),
}))

const workspaceRelationships = relationships(workspace, ({ many, one }) => ({
	memberships: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: membership,
	}),
	feedbacks: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: feedback,
	}),
	changelogs: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: changelog,
	}),
	changelogTags: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: changelogTag,
	}),
	helpCenterArticles: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: helpCenterArticle,
	}),
	helpCenterArticleProposals: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: helpCenterArticleProposal,
	}),
	helpCenterArticleProposalTranslations: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: helpCenterArticleProposalTranslation,
	}),
	helpCenterProposalGroups: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: helpCenterProposalGroup,
	}),
	helpCenterGroups: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: helpCenterGroup,
	}),
	issues: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: issue,
	}),
	projects: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: project,
	}),
	tagGroups: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: tagGroup,
	}),
	companies: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: company,
	}),
	contacts: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: contact,
	}),
	emails: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: email,
	}),
	threadMessages: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: threadMessage,
	}),
	threadActivities: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: threadActivity,
	}),
	slackMessages: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: slackMessage,
	}),
	chatMessages: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: chatMessage,
	}),
	roadmapSettings: one({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: roadmapSettings,
	}),
	emailSettings: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: emailSettings,
	}),
	slackConnectSettings: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: slackConnectSettings,
	}),
	customerPortalSettings: one({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: customerPortalSettings,
	}),
	// Reconnecting deletes and recreates these rows. Read them with .one(): a one()
	// relation crashes a client that gets the new row before the old one's delete.
	intercomSettings: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: intercomSettings,
	}),
	hubspotSettings: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: hubspotSettings,
	}),
	plainSettings: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: plainSettings,
	}),
	teamsSettings: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: teamsSettings,
	}),
	githubSettings: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: githubSettings,
	}),
	widgetSettings: one({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: widgetSettings,
	}),
	aiChatSettings: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: aiChatSettings,
	}),
	liveChatSessions: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: liveChatSession,
	}),
	upvotes: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: upvote,
	}),
	issueUpvotes: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: issueUpvote,
	}),
	tags: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: tag,
	}),
	sidebarSections: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: sidebarSection,
	}),
	customViews: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: customView,
	}),
	slackUsers: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: slackUser,
	}),
	ssoSettings: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: ssoSettings,
	}),
	feedbackTemplates: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: feedbackTemplate,
	}),
	snippets: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: snippet,
	}),
	snippetFolders: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: snippetFolder,
	}),
	videos: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: video,
	}),
	widgetLinks: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: widgetLink,
	}),
	issueTranslations: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: issueTranslation,
	}),
	projectTranslations: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: projectTranslation,
	}),
	changelogTranslations: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: changelogTranslation,
	}),
	helpCenterArticleTranslations: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: helpCenterArticleTranslation,
	}),
	helpCenterGroupTranslations: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: helpCenterGroupTranslation,
	}),
	helpCenterHomeTranslations: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: helpCenterHomeTranslation,
	}),
	aiChatSettingsTranslations: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: aiChatSettingsTranslation,
	}),
	blockedSenders: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: blockedSender,
	}),
	forwardedEmailIgnoreSenders: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: forwardedEmailIgnoreSender,
	}),
	portalInstances: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: portalInstance,
	}),
	slaPolicies: many({
		sourceField: ["id"],
		destField: ["workspaceId"],
		destSchema: slaPolicy,
	}),
}))

const membershipRelationships = relationships(membership, ({ one }) => ({
	user: one({
		sourceField: ["userId"],
		destField: ["id"],
		destSchema: user,
	}),
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	personalSlackNotificationSettings: one({
		sourceField: ["id"],
		destField: ["membershipId"],
		destSchema: personalSlackNotificationSettings,
	}),
}))

const feedbackRelationships = relationships(feedback, ({ one, many }) => ({
	// The stored Slack channel a Slack thread came from; its Slack workspace drives the inbox filter.
	slackChannel: many({
		sourceField: ["workspaceId", "slackChannelId"],
		destField: ["workspaceId", "channelId"],
		destSchema: slackChannels,
	}),
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	reporter: one({
		sourceField: ["reporterId"],
		destField: ["id"],
		destSchema: user,
	}),
	assignee: one({
		sourceField: ["assigneeId"],
		destField: ["id"],
		destSchema: user,
	}),
	comments: many({
		sourceField: ["id"],
		destField: ["feedbackId"],
		destSchema: comment,
	}),
	emails: many({
		sourceField: ["id"],
		destField: ["threadId"],
		destSchema: email,
	}),
	slackMessages: many({
		sourceField: ["id"],
		destField: ["threadId"],
		destSchema: slackMessage,
	}),
	chatMessages: many({
		sourceField: ["id"],
		destField: ["threadId"],
		destSchema: chatMessage,
	}),
	threadMessages: many({
		sourceField: ["id"],
		destField: ["threadId"],
		destSchema: threadMessage,
	}),
	threadActivities: many({
		sourceField: ["id"],
		destField: ["threadId"],
		destSchema: threadActivity,
	}),
	aiChatSessions: many({
		sourceField: ["id"],
		destField: ["threadId"],
		destSchema: aiChatSession,
	}),
	tags: many(
		{
			sourceField: ["id"],
			destSchema: feedbackToTag,
			destField: ["A"],
		},
		{
			sourceField: ["B"],
			destSchema: tag,
			destField: ["id"],
		},
	),
	issues: many(
		{
			sourceField: ["id"],
			destSchema: feedbackToIssue,
			destField: ["A"],
		},
		{
			sourceField: ["B"],
			destSchema: issue,
			destField: ["id"],
		},
	),
	projects: many(
		{
			sourceField: ["id"],
			destSchema: feedbackToProject,
			destField: ["A"],
		},
		{
			sourceField: ["B"],
			destSchema: project,
			destField: ["id"],
		},
	),
	contact: one({
		sourceField: ["contactId"],
		destField: ["id"],
		destSchema: contact,
	}),
	liveChatSession: one({
		sourceField: ["id"],
		destField: ["threadId"],
		destSchema: liveChatSession,
	}),
	video: one({
		sourceField: ["videoId"],
		destField: ["id"],
		destSchema: video,
	}),
	company: one({
		sourceField: ["companyId"],
		destField: ["id"],
		destSchema: company,
	}),
}))

const companyRelationships = relationships(company, ({ one, many }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	contacts: many({
		sourceField: ["id"],
		destField: ["companyId"],
		destSchema: contact,
	}),
	contactLinks: many({
		sourceField: ["id"],
		destField: ["companyId"],
		destSchema: contactCompany,
	}),
	linkedContacts: many(
		{
			sourceField: ["id"],
			destSchema: contactCompany,
			destField: ["companyId"],
		},
		{
			sourceField: ["contactId"],
			destSchema: contact,
			destField: ["id"],
		},
	),
	feedbacks: many({
		sourceField: ["id"],
		destField: ["companyId"],
		destSchema: feedback,
	}),
	companyMeta: one({
		sourceField: ["domain"],
		destField: ["domain"],
		destSchema: companyMeta,
	}),
	segments: many(
		{
			sourceField: ["id"],
			destSchema: companyToSegment,
			destField: ["A"],
		},
		{
			sourceField: ["B"],
			destSchema: segment,
			destField: ["id"],
		},
	),
}))

const contactRelationships = relationships(contact, ({ one, many }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	company: one({
		sourceField: ["companyId"],
		destField: ["id"],
		destSchema: company,
	}),
	feedbacks: many({
		sourceField: ["id"],
		destField: ["contactId"],
		destSchema: feedback,
	}),

	segments: many(
		{
			sourceField: ["id"],
			destSchema: contactToSegment,
			destField: ["A"],
		},
		{
			sourceField: ["B"],
			destSchema: segment,
			destField: ["id"],
		},
	),

	companyLinks: many({
		sourceField: ["id"],
		destField: ["contactId"],
		destSchema: contactCompany,
	}),
	linkedCompanies: many(
		{
			sourceField: ["id"],
			destSchema: contactCompany,
			destField: ["contactId"],
		},
		{
			sourceField: ["companyId"],
			destSchema: company,
			destField: ["id"],
		},
	),
}))

const contactCompanyRelationships = relationships(
	contactCompany,
	({ one }) => ({
		contact: one({
			sourceField: ["contactId"],
			destField: ["id"],
			destSchema: contact,
		}),
		company: one({
			sourceField: ["companyId"],
			destField: ["id"],
			destSchema: company,
		}),
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const tagGroupRelationships = relationships(tagGroup, ({ one, many }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	tags: many({
		sourceField: ["id"],
		destField: ["tagGroupId"],
		destSchema: tag,
	}),
}))

const tagRelationships = relationships(tag, ({ one, many }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	tagGroup: one({
		sourceField: ["tagGroupId"],
		destField: ["id"],
		destSchema: tagGroup,
	}),
	feedbacks: many(
		{
			sourceField: ["id"],
			destSchema: feedbackToTag,
			destField: ["B"],
		},
		{
			sourceField: ["A"],
			destSchema: feedback,
			destField: ["id"],
		},
	),
}))

const videoRelationships = relationships(video, ({ one, many }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	uploader: one({
		sourceField: ["uploaderId"],
		destField: ["id"],
		destSchema: user,
	}),
	recording: one({
		sourceField: ["recordingId"],
		destField: ["id"],
		destSchema: meetingRecording,
	}),
	transcriptUtterances: many({
		sourceField: ["id"],
		destField: ["videoId"],
		destSchema: transcriptUtterance,
	}),
}))

const meetingRecordingRelationships = relationships(
	meetingRecording,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		users: one({
			sourceField: ["userId"],
			destField: ["id"],
			destSchema: user,
		}),
		videos: many({
			sourceField: ["id"],
			destField: ["recordingId"],
			destSchema: video,
		}),
	}),
)

const transcriptUtteranceRelationships = relationships(
	transcriptUtterance,
	({ one }) => ({
		video: one({
			sourceField: ["videoId"],
			destField: ["id"],
			destSchema: video,
		}),
		speakerContact: one({
			sourceField: ["contactId"],
			destField: ["id"],
			destSchema: contact,
		}),
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const projectRelationships = relationships(project, ({ one, many }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	customerNeeds: many({
		sourceField: ["id"],
		destField: ["projectId"],
		destSchema: customerNeed,
	}),
	feedbacks: many(
		{
			sourceField: ["id"],
			destSchema: feedbackToProject,
			destField: ["B"],
		},
		{
			sourceField: ["A"],
			destSchema: feedback,
			destField: ["id"],
		},
	),
	linearRoadmaps: many(
		{
			sourceField: ["id"],
			destSchema: linearRoadmapToProject,
			destField: ["B"],
		},
		{
			sourceField: ["A"],
			destSchema: linearRoadmap,
			destField: ["id"],
		},
	),
	issues: many({
		sourceField: ["linearProjectId"],
		destSchema: issue,
		destField: ["projectId"],
	}),
	lead: one({
		sourceField: ["workspaceId", "leadId"],
		destField: ["workspaceId", "linearUserId"],
		destSchema: linearUser,
	}),
	translations: many({
		sourceField: ["id"],
		destField: ["projectId"],
		destSchema: projectTranslation,
	}),
	upvotes: many({
		sourceField: ["id"],
		destField: ["projectId"],
		destSchema: upvote,
	}),
}))

const issueRelationships = relationships(issue, ({ one, many }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	customerNeeds: many({
		sourceField: ["id"],
		destField: ["issueId"],
		destSchema: customerNeed,
	}),
	labels: many({
		sourceField: ["id"],
		destField: ["issueId"],
		destSchema: issueLabel,
	}),
	assignee: one({
		sourceField: ["workspaceId", "assigneeId"],
		destField: ["workspaceId", "linearUserId"],
		destSchema: linearUser,
	}),
	delegate: one({
		sourceField: ["workspaceId", "delegateId"],
		destField: ["workspaceId", "linearUserId"],
		destSchema: linearUser,
	}),
	cycle: one({
		sourceField: ["workspaceId", "cycleId"],
		destField: ["workspaceId", "linearCycleId"],
		destSchema: linearCycle,
	}),
	feedbacks: many(
		{
			sourceField: ["id"],
			destSchema: feedbackToIssue,
			destField: ["B"],
		},
		{
			sourceField: ["A"],
			destSchema: feedback,
			destField: ["id"],
		},
	),
	upvotes: many({
		sourceField: ["id"],
		destField: ["issueId"],
		destSchema: issueUpvote,
	}),
	translations: many({
		sourceField: ["id"],
		destField: ["issueId"],
		destSchema: issueTranslation,
	}),
	codingAgentRuns: many({
		sourceField: ["id"],
		destField: ["issueId"],
		destSchema: codingAgentRun,
	}),
	helpCenterProposalGroups: many(
		{
			sourceField: ["id"],
			destSchema: helpCenterProposalGroupToIssue,
			destField: ["B"],
		},
		{
			sourceField: ["A"],
			destSchema: helpCenterProposalGroup,
			destField: ["id"],
		},
	),
}))

const codingAgentRunRelationships = relationships(
	codingAgentRun,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		issue: one({
			sourceField: ["issueId"],
			destField: ["id"],
			destSchema: issue,
		}),
		triggeredBy: one({
			sourceField: ["triggeredById"],
			destField: ["id"],
			destSchema: user,
		}),
		feedback: one({
			sourceField: ["feedbackId"],
			destField: ["id"],
			destSchema: feedback,
		}),
	}),
)

const customerNeedRelationships = relationships(customerNeed, ({ one }) => ({
	feedback: one({
		sourceField: ["feedbackId"],
		destField: ["id"],
		destSchema: feedback,
	}),
	issue: one({
		sourceField: ["issueId"],
		destField: ["id"],
		destSchema: issue,
	}),
}))

const changelogRelationships = relationships(changelog, ({ one, many }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	project: one({
		sourceField: ["projectId"],
		destField: ["id"],
		destSchema: project,
	}),
	versions: many({
		sourceField: ["id"],
		destField: ["changelogId"],
		destSchema: changelogVersion,
	}),
	translations: many({
		sourceField: ["id"],
		destField: ["changelogId"],
		destSchema: changelogTranslation,
	}),
	changelogTags: many(
		{
			sourceField: ["id"],
			destSchema: changelogToChangelogTag,
			destField: ["A"],
		},
		{
			sourceField: ["B"],
			destSchema: changelogTag,
			destField: ["id"],
		},
	),
	portalInstance: one({
		sourceField: ["portalInstanceId"],
		destField: ["id"],
		destSchema: portalInstance,
	}),
}))

const portalInstanceRelationships = relationships(
	portalInstance,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		changelogs: many({
			sourceField: ["id"],
			destField: ["portalInstanceId"],
			destSchema: changelog,
		}),
		helpCenterArticles: many({
			sourceField: ["id"],
			destField: ["portalInstanceId"],
			destSchema: helpCenterArticle,
		}),
		ssoSettings: many({
			sourceField: ["id"],
			destField: ["portalInstanceId"],
			destSchema: ssoSettings,
		}),
	}),
)

const changelogTagRelationships = relationships(
	changelogTag,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		changelogs: many(
			{
				sourceField: ["id"],
				destSchema: changelogToChangelogTag,
				destField: ["B"],
			},
			{
				sourceField: ["A"],
				destSchema: changelog,
				destField: ["id"],
			},
		),
	}),
)

const helpCenterGroupRelationships = relationships(
	helpCenterGroup,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		helpCenterArticles: many({
			sourceField: ["id"],
			destField: ["helpCenterGroupId"],
			destSchema: helpCenterArticle,
		}),
		translations: many({
			sourceField: ["id"],
			destField: ["helpCenterGroupId"],
			destSchema: helpCenterGroupTranslation,
		}),
		tab: one({
			sourceField: ["tabId"],
			destField: ["id"],
			destSchema: helpCenterTab,
		}),
	}),
)

const helpCenterCustomHomeSectionRelationships = relationships(
	helpCenterCustomHomeSection,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		group: one({
			sourceField: ["groupId"],
			destField: ["id"],
			destSchema: helpCenterGroup,
		}),
		articles: many({
			sourceField: ["id"],
			destField: ["customHomeSectionId"],
			destSchema: helpCenterArticle,
		}),
	}),
)

const helpCenterTabRelationships = relationships(
	helpCenterTab,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		groups: many({
			sourceField: ["id"],
			destField: ["tabId"],
			destSchema: helpCenterGroup,
		}),
	}),
)

const helpCenterArticleRelationships = relationships(
	helpCenterArticle,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		group: one({
			sourceField: ["helpCenterGroupId"],
			destField: ["id"],
			destSchema: helpCenterGroup,
		}),
		customHomeSection: one({
			sourceField: ["customHomeSectionId"],
			destField: ["id"],
			destSchema: helpCenterCustomHomeSection,
		}),
		versions: many({
			sourceField: ["id"],
			destField: ["articleId"],
			destSchema: helpCenterArticleVersion,
		}),
		translations: many({
			sourceField: ["id"],
			destField: ["helpCenterArticleId"],
			destSchema: helpCenterArticleTranslation,
		}),
		parent: one({
			sourceField: ["parentArticleId"],
			destField: ["id"],
			destSchema: helpCenterArticle,
		}),
		subpages: many({
			sourceField: ["id"],
			destField: ["parentArticleId"],
			destSchema: helpCenterArticle,
		}),
		proposals: many({
			sourceField: ["id"],
			destField: ["articleId"],
			destSchema: helpCenterArticleProposal,
		}),
		portalInstance: one({
			sourceField: ["portalInstanceId"],
			destField: ["id"],
			destSchema: portalInstance,
		}),
	}),
)

const helpCenterArticleProposalRelationships = relationships(
	helpCenterArticleProposal,
	({ many, one }) => ({
		translations: many({
			sourceField: ["id"],
			destField: ["proposalId"],
			destSchema: helpCenterArticleProposalTranslation,
		}),
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		article: one({
			sourceField: ["articleId"],
			destField: ["id"],
			destSchema: helpCenterArticle,
		}),
		group: one({
			sourceField: ["proposalGroupId"],
			destField: ["id"],
			destSchema: helpCenterProposalGroup,
		}),
		// The docs folder a CREATE proposal files its new article under.
		proposedGroup: one({
			sourceField: ["proposedGroupId"],
			destField: ["id"],
			destSchema: helpCenterGroup,
		}),
		baseVersion: one({
			sourceField: ["baseVersionId"],
			destField: ["id"],
			destSchema: helpCenterArticleVersion,
		}),
		createdBy: one({
			sourceField: ["createdByUserId"],
			destField: ["id"],
			destSchema: user,
		}),
		closedBy: one({
			sourceField: ["closedByUserId"],
			destField: ["id"],
			destSchema: user,
		}),
	}),
)

const helpCenterArticleVersionRelationships = relationships(
	helpCenterArticleVersion,
	({ one }) => ({
		article: one({
			sourceField: ["articleId"],
			destField: ["id"],
			destSchema: helpCenterArticle,
		}),
		parentVersion: one({
			sourceField: ["parentVersionId"],
			destField: ["id"],
			destSchema: helpCenterArticleVersion,
		}),
		createdBy: one({
			sourceField: ["createdById"],
			destField: ["id"],
			destSchema: user,
		}),
	}),
)

const helpCenterProposalGroupRelationships = relationships(
	helpCenterProposalGroup,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		proposals: many({
			sourceField: ["id"],
			destField: ["proposalGroupId"],
			destSchema: helpCenterArticleProposal,
		}),
		issues: many(
			{
				sourceField: ["id"],
				destSchema: helpCenterProposalGroupToIssue,
				destField: ["A"],
			},
			{
				sourceField: ["B"],
				destSchema: issue,
				destField: ["id"],
			},
		),
		sourceThreads: many(
			{
				sourceField: ["id"],
				destSchema: helpCenterProposalGroupToThread,
				destField: ["B"],
			},
			{
				sourceField: ["A"],
				destSchema: feedback,
				destField: ["id"],
			},
		),
	}),
)

const helpCenterProposalGroupToThreadRelationships = relationships(
	helpCenterProposalGroupToThread,
	({ one }) => ({
		thread: one({
			sourceField: ["A"],
			destField: ["id"],
			destSchema: feedback,
		}),
		group: one({
			sourceField: ["B"],
			destField: ["id"],
			destSchema: helpCenterProposalGroup,
		}),
	}),
)

const helpCenterProposalGroupToIssueRelationships = relationships(
	helpCenterProposalGroupToIssue,
	({ one }) => ({
		group: one({
			sourceField: ["A"],
			destField: ["id"],
			destSchema: helpCenterProposalGroup,
		}),
		issue: one({
			sourceField: ["B"],
			destField: ["id"],
			destSchema: issue,
		}),
	}),
)

const agentRecommendationRelationships = relationships(
	agentRecommendation,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		proposalGroup: one({
			sourceField: ["proposalGroupId"],
			destField: ["id"],
			destSchema: helpCenterProposalGroup,
		}),
	}),
)

const suggestionRelationships = relationships(suggestion, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	thread: one({
		sourceField: ["threadId"],
		destField: ["id"],
		destSchema: feedback,
	}),
	targetIssue: one({
		sourceField: ["targetId"],
		destField: ["id"],
		destSchema: issue,
	}),
	targetProject: one({
		sourceField: ["targetId"],
		destField: ["id"],
		destSchema: project,
	}),
	targetTag: one({
		sourceField: ["targetId"],
		destField: ["id"],
		destSchema: tag,
	}),
}))

const widgetSettingsRelationships = relationships(
	widgetSettings,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		widgetContactAvatars: many({
			sourceField: ["id"],
			destField: ["widgetSettingsId"],
			destSchema: widgetContactAvatar,
		}),
	}),
)

const widgetContactAvatarRelationships = relationships(
	widgetContactAvatar,
	({ one }) => ({
		widgetSettings: one({
			sourceField: ["widgetSettingsId"],
			destField: ["id"],
			destSchema: widgetSettings,
		}),
		membership: one({
			sourceField: ["membershipId"],
			destField: ["id"],
			destSchema: membership,
		}),
	}),
)

const aiChatSettingsRelationships = relationships(
	aiChatSettings,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const widgetLinkRelationships = relationships(widgetLink, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const customViewRelationships = relationships(customView, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const issueLabelRelationships = relationships(issueLabel, ({ one }) => ({
	label: one({
		sourceField: ["labelId"],
		destField: ["id"],
		destSchema: linearLabel,
	}),
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const linearLabelRelationships = relationships(linearLabel, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const sidebarSectionRelationships = relationships(
	sidebarSection,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		sidebarSectionHeaders: many({
			sourceField: ["id"],
			destField: ["sidebarSectionId"],
			destSchema: sidebarSectionHeader,
		}),
	}),
)

const sidebarSectionHeaderRelationships = relationships(
	sidebarSectionHeader,
	({ one }) => ({
		section: one({
			sourceField: ["sidebarSectionId"],
			destField: ["id"],
			destSchema: sidebarSection,
		}),
	}),
)

const workspaceInviteRelationships = relationships(
	workspaceInvite,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		invitedBy: one({
			sourceField: ["invitedById"],
			destField: ["id"],
			destSchema: user,
		}),
	}),
)

const emailSettingsRelationships = relationships(emailSettings, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const blockedSenderRelationships = relationships(blockedSender, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const forwardedEmailIgnoreSenderRelationships = relationships(
	forwardedEmailIgnoreSender,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const personalSlackNotificationSettingsRelationships = relationships(
	personalSlackNotificationSettings,
	({ one }) => ({
		membership: one({
			sourceField: ["membershipId"],
			destField: ["id"],
			destSchema: membership,
		}),
	}),
)

const slackSettingsRelationships = relationships(slackSettings, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const slackConnectSettingsRelationships = relationships(
	slackConnectSettings,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		channels: many({
			sourceField: ["id"],
			destField: ["slackConnectSettingsId"],
			destSchema: slackChannels,
		}),
	}),
)

const slackChannelsRelationships = relationships(slackChannels, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	slackConnectSettings: one({
		sourceField: ["slackConnectSettingsId"],
		destField: ["id"],
		destSchema: slackConnectSettings,
	}),
	tag: one({
		sourceField: ["tagId"],
		destField: ["id"],
		destSchema: tag,
	}),
	assignee: one({
		sourceField: ["assigneeId"],
		destField: ["id"],
		destSchema: user,
	}),
}))

const ssoSettingsRelationships = relationships(ssoSettings, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const intercomSettingsRelationships = relationships(
	intercomSettings,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const hubspotSettingsRelationships = relationships(
	hubspotSettings,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const plainSettingsRelationships = relationships(plainSettings, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const teamsSettingsRelationships = relationships(teamsSettings, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const githubSettingsRelationships = relationships(
	githubSettings,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const threadMessageRelationships = relationships(threadMessage, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	thread: one({
		sourceField: ["threadId"],
		destField: ["id"],
		destSchema: feedback,
	}),
}))

const threadActivityRelationships = relationships(
	threadActivity,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		thread: one({
			sourceField: ["threadId"],
			destField: ["id"],
			destSchema: feedback,
		}),
		actor: one({
			sourceField: ["actorId"],
			destField: ["id"],
			destSchema: user,
		}),
	}),
)

const notificationRelationships = relationships(notification, ({ one }) => ({
	actor: one({
		sourceField: ["actorId"],
		destField: ["id"],
		destSchema: user,
	}),
	thread: one({
		sourceField: ["threadId"],
		destField: ["id"],
		destSchema: feedback,
	}),
}))

const notificationSubscriptionRelationships = relationships(
	notificationSubscription,
	({ one }) => ({
		user: one({
			sourceField: ["userId"],
			destField: ["id"],
			destSchema: user,
		}),
	}),
)

const emailRelationships = relationships(email, ({ one }) => ({
	content: one({
		sourceField: ["id"],
		destField: ["emailId"],
		destSchema: emailContent,
	}),
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	thread: one({
		sourceField: ["threadId"],
		destField: ["id"],
		destSchema: feedback,
	}),
	user: one({
		sourceField: ["userId"],
		destField: ["id"],
		destSchema: user,
	}),
}))

const attachmentRelationships = relationships(attachment, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),

	uploader: one({
		sourceField: ["uploaderId"],
		destField: ["id"],
		destSchema: user,
	}),
}))

const slackUserRelationships = relationships(slackUser, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const linearUserRelationships = relationships(linearUser, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const linearCycleRelationships = relationships(linearCycle, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const slackMessageRelationships = relationships(slackMessage, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	thread: one({
		sourceField: ["threadId"],
		destField: ["id"],
		destSchema: feedback,
	}),
	user: one({
		sourceField: ["userId"],
		destField: ["id"],
		destSchema: user,
	}),
}))

const messageReactionRelationships = relationships(
	messageReaction,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		thread: one({
			sourceField: ["threadId"],
			destField: ["id"],
			destSchema: feedback,
		}),
		user: one({
			sourceField: ["userId"],
			destField: ["id"],
			destSchema: user,
		}),
	}),
)

const chatMessageRelationships = relationships(chatMessage, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	thread: one({
		sourceField: ["threadId"],
		destField: ["id"],
		destSchema: feedback,
	}),
	user: one({
		sourceField: ["userId"],
		destField: ["id"],
		destSchema: user,
	}),
}))

const snippetRelationships = relationships(snippet, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	email: one({
		sourceField: ["emailId"],
		destField: ["id"],
		destSchema: email,
	}),
	folder: one({
		sourceField: ["folderId"],
		destField: ["id"],
		destSchema: snippetFolder,
	}),
}))

const snippetFolderRelationships = relationships(
	snippetFolder,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		snippets: many({
			sourceField: ["id"],
			destField: ["folderId"],
			destSchema: snippet,
		}),
	}),
)

const slaPolicyRelationships = relationships(slaPolicy, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const apiKeyRelationships = relationships(apiKey, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	createdBy: one({
		sourceField: ["createdById"],
		destField: ["id"],
		destSchema: user,
	}),
}))

const oauthClientRelationships = relationships(oauthClient, ({ one }) => ({
	ownerWorkspace: one({
		sourceField: ["ownerWorkspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const webhookRelationships = relationships(webhook, ({ many, one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	createdBy: one({
		sourceField: ["createdById"],
		destField: ["id"],
		destSchema: user,
	}),
	deliveries: many({
		sourceField: ["id"],
		destField: ["webhookId"],
		destSchema: webhookDelivery,
	}),
}))

const webhookDeliveryRelationships = relationships(
	webhookDelivery,
	({ one }) => ({
		webhook: one({
			sourceField: ["webhookId"],
			destField: ["id"],
			destSchema: webhook,
		}),
	}),
)

const allowedEmailDomainRelationships = relationships(
	allowedEmailDomain,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const feedbackTemplateRelationships = relationships(
	feedbackTemplate,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const companyMetaRelationships = relationships(companyMeta, ({ many }) => ({
	companies: many({
		sourceField: ["domain"],
		destField: ["domain"],
		destSchema: company,
	}),
}))

const segmentRelationships = relationships(segment, ({ one, many }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	companies: many(
		{
			sourceField: ["id"],
			destSchema: companyToSegment,
			destField: ["B"],
		},
		{
			sourceField: ["A"],
			destSchema: company,
			destField: ["id"],
		},
	),
	contacts: many(
		{
			sourceField: ["id"],
			destSchema: contactToSegment,
			destField: ["B"],
		},
		{
			sourceField: ["A"],
			destSchema: contact,
			destField: ["id"],
		},
	),
}))

const upvoteRelationships = relationships(upvote, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	project: one({
		sourceField: ["projectId"],
		destField: ["id"],
		destSchema: project,
	}),
	contact: one({
		sourceField: ["contactId"],
		destField: ["id"],
		destSchema: contact,
	}),
}))

const issueUpvoteRelationships = relationships(issueUpvote, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	issue: one({
		sourceField: ["issueId"],
		destField: ["id"],
		destSchema: issue,
	}),
	contact: one({
		sourceField: ["contactId"],
		destField: ["id"],
		destSchema: contact,
	}),
}))

const roadmapSettingsRelationships = relationships(
	roadmapSettings,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const customerPortalSettingsRelationships = relationships(
	customerPortalSettings,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const linearRoadmapRelationships = relationships(linearRoadmap, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
}))

const commentRelationships = relationships(comment, ({ one, many }) => ({
	feedback: one({
		sourceField: ["feedbackId"],
		destField: ["id"],
		destSchema: feedback,
	}),
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	user: one({
		sourceField: ["userId"],
		destField: ["id"],
		destSchema: user,
	}),
	parent: one({
		sourceField: ["parentId"],
		destField: ["id"],
		destSchema: comment,
	}),
	replies: many({
		sourceField: ["id"],
		destField: ["parentId"],
		destSchema: comment,
	}),
}))

const companyToSegmentRelationships = relationships(
	companyToSegment,
	({ one }) => ({
		company: one({
			sourceField: ["A"],
			destField: ["id"],
			destSchema: company,
		}),
		segment: one({
			sourceField: ["B"],
			destField: ["id"],
			destSchema: segment,
		}),
	}),
)

const contactToSegmentRelationships = relationships(
	contactToSegment,
	({ one }) => ({
		contact: one({
			sourceField: ["A"],
			destField: ["id"],
			destSchema: contact,
		}),
		segment: one({
			sourceField: ["B"],
			destField: ["id"],
			destSchema: segment,
		}),
	}),
)

const changelogToChangelogTagRelationships = relationships(
	changelogToChangelogTag,
	({ one }) => ({
		changelog: one({
			sourceField: ["A"],
			destField: ["id"],
			destSchema: changelog,
		}),
		changelogTag: one({
			sourceField: ["B"],
			destField: ["id"],
			destSchema: changelogTag,
		}),
	}),
)

const feedbackToTagRelationships = relationships(feedbackToTag, ({ one }) => ({
	feedback: one({
		sourceField: ["A"],
		destField: ["id"],
		destSchema: feedback,
	}),
	tag: one({
		sourceField: ["B"],
		destField: ["id"],
		destSchema: tag,
	}),
}))

const linearRoadmapToProjectRelationships = relationships(
	linearRoadmapToProject,
	({ one }) => ({
		project: one({
			sourceField: ["B"],
			destField: ["id"],
			destSchema: project,
		}),
		linearRoadmap: one({
			sourceField: ["A"],
			destField: ["id"],
			destSchema: linearRoadmap,
		}),
	}),
)

const feedbackToIssueRelationships = relationships(
	feedbackToIssue,
	({ one }) => ({
		feedback: one({
			sourceField: ["A"],
			destField: ["id"],
			destSchema: feedback,
		}),
		issue: one({
			sourceField: ["B"],
			destField: ["id"],
			destSchema: issue,
		}),
	}),
)

const feedbackToProjectRelationships = relationships(
	feedbackToProject,
	({ one }) => ({
		feedback: one({
			sourceField: ["A"],
			destField: ["id"],
			destSchema: feedback,
		}),
		project: one({
			sourceField: ["B"],
			destField: ["id"],
			destSchema: project,
		}),
	}),
)

const aiChatSessionRelationships = relationships(aiChatSession, ({ one }) => ({
	workspace: one({
		sourceField: ["workspaceId"],
		destField: ["id"],
		destSchema: workspace,
	}),
	thread: one({
		sourceField: ["threadId"],
		destField: ["id"],
		destSchema: feedback,
	}),
}))

const liveChatSessionRelationships = relationships(
	liveChatSession,
	({ one, many }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		thread: one({
			sourceField: ["threadId"],
			destField: ["id"],
			destSchema: feedback,
		}),
		messages: many({
			sourceField: ["id"],
			destField: ["sessionId"],
			destSchema: liveChatMessage,
		}),
	}),
)

const liveChatMessageRelationships = relationships(
	liveChatMessage,
	({ one }) => ({
		session: one({
			sourceField: ["sessionId"],
			destField: ["id"],
			destSchema: liveChatSession,
		}),
	}),
)

const changelogVersionRelationships = relationships(
	changelogVersion,
	({ one }) => ({
		changelog: one({
			sourceField: ["changelogId"],
			destField: ["id"],
			destSchema: changelog,
		}),
		createdBy: one({
			sourceField: ["createdById"],
			destField: ["id"],
			destSchema: user,
		}),
	}),
)

const issueTranslationRelationships = relationships(
	issueTranslation,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		issue: one({
			sourceField: ["issueId"],
			destField: ["id"],
			destSchema: issue,
		}),
	}),
)

const projectTranslationRelationships = relationships(
	projectTranslation,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		project: one({
			sourceField: ["projectId"],
			destField: ["id"],
			destSchema: project,
		}),
	}),
)

const changelogTranslationRelationships = relationships(
	changelogTranslation,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		changelog: one({
			sourceField: ["changelogId"],
			destField: ["id"],
			destSchema: changelog,
		}),
	}),
)

const helpCenterArticleTranslationRelationships = relationships(
	helpCenterArticleTranslation,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		article: one({
			sourceField: ["helpCenterArticleId"],
			destField: ["id"],
			destSchema: helpCenterArticle,
		}),
	}),
)

const helpCenterArticleProposalTranslationRelationships = relationships(
	helpCenterArticleProposalTranslation,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		proposal: one({
			sourceField: ["proposalId"],
			destField: ["id"],
			destSchema: helpCenterArticleProposal,
		}),
	}),
)

const helpCenterGroupTranslationRelationships = relationships(
	helpCenterGroupTranslation,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
		group: one({
			sourceField: ["helpCenterGroupId"],
			destField: ["id"],
			destSchema: helpCenterGroup,
		}),
	}),
)

const helpCenterHomeTranslationRelationships = relationships(
	helpCenterHomeTranslation,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

const aiChatSettingsTranslationRelationships = relationships(
	aiChatSettingsTranslation,
	({ one }) => ({
		workspace: one({
			sourceField: ["workspaceId"],
			destField: ["id"],
			destSchema: workspace,
		}),
	}),
)

// Create schema
export const schema = createSchema({
	enableLegacyQueries: true,
	tables: [
		linearLabel,
		issueLabel,
		user,
		workspace,
		membership,
		feedback,
		company,
		contact,
		tagGroup,
		tag,
		video,
		meetingRecording,
		transcriptUtterance,
		project,
		issue,
		customerNeed,
		changelog,
		helpCenterGroup,
		helpCenterTab,
		helpCenterArticle,
		helpCenterCustomHomeSection,
		widgetSettings,
		widgetContactAvatar,
		aiChatSettings,
		widgetLink,
		customView,
		sidebarSection,
		sidebarSectionHeader,
		workspaceInvite,
		emailSettings,
		blockedSender,
		forwardedEmailIgnoreSender,
		personalSlackNotificationSettings,
		slackSettings,
		slackConnectSettings,
		slackChannels,
		ssoSettings,
		intercomSettings,
		hubspotSettings,
		plainSettings,
		teamsSettings,
		githubSettings,
		threadMessage,
		threadActivity,
		notification,
		notificationSubscription,
		notificationPreference,
		email,
		emailContent,
		attachment,
		slackUser,
		linearUser,
		linearCycle,
		slackMessage,
		messageReaction,
		chatMessage,
		snippet,
		snippetFolder,
		allowedEmailDomain,
		feedbackTemplate,
		companyMeta,
		segment,
		upvote,
		issueUpvote,
		roadmapSettings,
		customerPortalSettings,
		linearRoadmap,
		comment,
		companyToSegment,
		contactCompany,
		contactToSegment,
		changelogTag,
		changelogToChangelogTag,
		feedbackToTag,
		feedbackToIssue,
		feedbackToProject,
		linearRoadmapToProject,
		aiChatSession,
		liveChatSession,
		liveChatMessage,
		changelogVersion,
		helpCenterArticleVersion,
		helpCenterArticleProposal,
		helpCenterProposalGroup,
		helpCenterProposalGroupToIssue,
		helpCenterProposalGroupToThread,
		agentRecommendation,
		suggestion,
		issueTranslation,
		projectTranslation,
		changelogTranslation,
		helpCenterArticleTranslation,
		helpCenterArticleProposalTranslation,
		helpCenterGroupTranslation,
		helpCenterHomeTranslation,
		aiChatSettingsTranslation,
		slaPolicy,
		codingAgentRun,
		apiKey,
		webhook,
		webhookDelivery,
		oauthClient,
		portalInstance,
	],
	relationships: [
		issueLabelRelationships,
		linearLabelRelationships,
		userRelationships,
		workspaceRelationships,
		membershipRelationships,
		feedbackRelationships,
		companyRelationships,
		contactCompanyRelationships,
		contactRelationships,
		tagGroupRelationships,
		tagRelationships,
		videoRelationships,
		meetingRecordingRelationships,
		transcriptUtteranceRelationships,
		projectRelationships,
		issueRelationships,
		customerNeedRelationships,
		changelogRelationships,
		changelogTagRelationships,
		helpCenterGroupRelationships,
		helpCenterTabRelationships,
		helpCenterArticleRelationships,
		helpCenterCustomHomeSectionRelationships,
		widgetSettingsRelationships,
		widgetContactAvatarRelationships,
		aiChatSettingsRelationships,
		widgetLinkRelationships,
		customViewRelationships,
		sidebarSectionRelationships,
		sidebarSectionHeaderRelationships,
		workspaceInviteRelationships,
		emailSettingsRelationships,
		blockedSenderRelationships,
		forwardedEmailIgnoreSenderRelationships,
		personalSlackNotificationSettingsRelationships,
		slackSettingsRelationships,
		slackConnectSettingsRelationships,
		slackChannelsRelationships,
		ssoSettingsRelationships,
		intercomSettingsRelationships,
		hubspotSettingsRelationships,
		plainSettingsRelationships,
		teamsSettingsRelationships,
		githubSettingsRelationships,
		threadMessageRelationships,
		threadActivityRelationships,
		notificationRelationships,
		notificationSubscriptionRelationships,
		emailRelationships,
		attachmentRelationships,
		slackUserRelationships,
		linearUserRelationships,
		linearCycleRelationships,
		slackMessageRelationships,
		messageReactionRelationships,
		chatMessageRelationships,
		snippetRelationships,
		snippetFolderRelationships,
		allowedEmailDomainRelationships,
		feedbackTemplateRelationships,
		companyMetaRelationships,
		segmentRelationships,
		upvoteRelationships,
		issueUpvoteRelationships,
		roadmapSettingsRelationships,
		customerPortalSettingsRelationships,
		linearRoadmapRelationships,
		commentRelationships,
		companyToSegmentRelationships,
		contactToSegmentRelationships,
		changelogToChangelogTagRelationships,
		feedbackToTagRelationships,
		feedbackToIssueRelationships,
		feedbackToProjectRelationships,
		linearRoadmapToProjectRelationships,
		aiChatSessionRelationships,
		liveChatSessionRelationships,
		liveChatMessageRelationships,
		changelogVersionRelationships,
		helpCenterArticleVersionRelationships,
		helpCenterArticleProposalRelationships,
		helpCenterProposalGroupRelationships,
		helpCenterProposalGroupToIssueRelationships,
		helpCenterProposalGroupToThreadRelationships,
		agentRecommendationRelationships,
		suggestionRelationships,
		issueTranslationRelationships,
		projectTranslationRelationships,
		changelogTranslationRelationships,
		helpCenterArticleTranslationRelationships,
		helpCenterArticleProposalTranslationRelationships,
		helpCenterGroupTranslationRelationships,
		helpCenterHomeTranslationRelationships,
		aiChatSettingsTranslationRelationships,
		slaPolicyRelationships,
		codingAgentRunRelationships,
		apiKeyRelationships,
		oauthClientRelationships,
		webhookRelationships,
		webhookDeliveryRelationships,
		portalInstanceRelationships,
	],
})

export type Schema = typeof schema

declare module "@rocicorp/zero" {
	interface DefaultTypes {
		schema: Schema
		context: ZeroJwtPayload
	}
}

export type MutatorTx = Transaction<Schema>
export type User = Row<typeof schema.tables.users>
export type Workspace = Row<typeof schema.tables.workspaces>
export type Membership = Row<typeof schema.tables.memberships>
export type Feedback = Row<typeof schema.tables.feedbacks>
export type Company = Row<typeof schema.tables.companies>
export type Contact = Row<typeof schema.tables.contacts>
export type TagGroup = Row<typeof schema.tables.tagGroups>
export type Tag = Row<typeof schema.tables.tags>
export type Video = Row<typeof schema.tables.videos>
export type MeetingRecording = Row<typeof schema.tables.meetingRecordings>
export type TranscriptUtterance = Row<typeof schema.tables.transcriptUtterances>
export type Project = Row<typeof schema.tables.projects>
export type Issue = Row<typeof schema.tables.issues>
export type CustomerNeed = Row<typeof schema.tables.customerNeeds>
export type Changelog = Row<typeof schema.tables.changelogs>
export type ChangelogTag = Row<typeof schema.tables.changelogTags>
export type PortalInstance = Row<typeof schema.tables.portalInstances>
export type HelpCenterGroup = Row<typeof schema.tables.helpCenterGroups>
export type HelpCenterTab = Row<typeof schema.tables.helpCenterTabs>
export type HelpCenterArticle = Row<typeof schema.tables.helpCenterArticles>
export type HelpCenterCustomHomeSection = Row<
	typeof schema.tables.helpCenterCustomHomeSections
>
export type WidgetSettings = Row<typeof schema.tables.widgetSettings>
export type WidgetContactAvatar = Row<typeof schema.tables.widgetContactAvatars>
export type AiChatSettings = Row<typeof schema.tables.aiChatSettings>
export type WidgetLink = Row<typeof schema.tables.widgetLinks>
export type CustomView = Row<typeof schema.tables.customViews>
export type SidebarSection = Row<typeof schema.tables.sidebarSections>
export type SidebarSectionHeader = Row<
	typeof schema.tables.sidebarSectionHeaders
>
export type WorkspaceInvite = Row<typeof schema.tables.workspaceInvites>
export type EmailSettings = Row<typeof schema.tables.emailSettings>
export type BlockedSender = Row<typeof schema.tables.blockedSender>
export type ForwardedEmailIgnoreSender = Row<
	typeof schema.tables.forwardedEmailIgnoreSender
>
export type PersonalSlackNotificationSettings = Row<
	typeof schema.tables.personalSlackNotificationSettings
>
export type SlackSettings = Row<typeof schema.tables.slackSettings>
export type SlackConnectSettings = Row<
	typeof schema.tables.slackConnectSettings
>
export type SlackChannel = Row<typeof schema.tables.slackChannels>
export type SsoSettings = Row<typeof schema.tables.ssoSettings>
export type IntercomSettings = Row<typeof schema.tables.intercomSettings>
export type HubspotSettings = Row<typeof schema.tables.hubspotSettings>
export type PlainSettings = Row<typeof schema.tables.plainSettings>
export type TeamsSettings = Row<typeof schema.tables.teamsSettings>
export type GithubSettings = Row<typeof schema.tables.githubSettings>
export type ThreadMessage = Row<typeof schema.tables.threadMessages>
export type ThreadActivityRow = Row<typeof schema.tables.threadActivities>
export type NotificationRow = Row<typeof schema.tables.notifications>
export type NotificationSubscriptionRow = Row<
	typeof schema.tables.notificationSubscriptions
>
export type NotificationPreferenceRow = Row<
	typeof schema.tables.notificationPreferences
>
export type Email = Row<typeof schema.tables.emails>
export type EmailContent = Row<typeof schema.tables.emailContents>
export type Attachment = Row<typeof schema.tables.attachments>
export type Snippet = Row<typeof schema.tables.snippets>
export type SnippetFolder = Row<typeof schema.tables.snippetFolders>
export type AllowedEmailDomain = Row<typeof schema.tables.allowedEmailDomains>
export type FeedbackTemplate = Row<typeof schema.tables.feedbackTemplates>
export type CompanyMeta = Row<typeof schema.tables.companyMetas>
export type Segment = Row<typeof schema.tables.segments>
export type Upvote = Row<typeof schema.tables.upvotes>
export type IssueUpvote = Row<typeof schema.tables.issueUpvotes>
export type RoadmapSettings = Row<typeof schema.tables.roadmapSettings>
export type CustomerPortalSettings = Row<
	typeof schema.tables.customerPortalSettings
>
export type LinearRoadmap = Row<typeof schema.tables.linearRoadmaps>
export type Comment = Row<typeof schema.tables.comments>
export type LinearUser = Row<typeof schema.tables.linearUsers>
export type LinearCycle = Row<typeof schema.tables.linearCycles>
export type FeedbackToProject = Row<typeof schema.tables.feedbackToProjects>
export type SlackMessage = Row<typeof schema.tables.slackMessages>
export type MessageReaction = Row<typeof schema.tables.messageReactions>
export type ChatMessage = Row<typeof schema.tables.chatMessages>
export type SlackUser = Row<typeof schema.tables.slackUsers>
export type AiChatSession = Row<typeof schema.tables.aiChatSessions>
export type LiveChatSession = Row<typeof schema.tables.liveChatSessions>
export type LiveChatMessage = Row<typeof schema.tables.liveChatMessages>
export type ChangelogVersion = Row<typeof schema.tables.changelogVersions>
export type HelpCenterArticleVersion = Row<
	typeof schema.tables.helpCenterArticleVersions
>
export type HelpCenterArticleProposal = Row<
	typeof schema.tables.helpCenterArticleProposals
>
export type HelpCenterProposalGroup = Row<
	typeof schema.tables.helpCenterProposalGroups
>
export type AgentRecommendation = Row<typeof schema.tables.agentRecommendations>
export type Suggestion = Row<typeof schema.tables.suggestions>
export type IssueTranslation = Row<typeof schema.tables.issueTranslations>
export type ProjectTranslation = Row<typeof schema.tables.projectTranslations>
export type ChangelogTranslation = Row<
	typeof schema.tables.changelogTranslations
>
export type HelpCenterArticleTranslation = Row<
	typeof schema.tables.helpCenterArticleTranslations
>
export type HelpCenterGroupTranslation = Row<
	typeof schema.tables.helpCenterGroupTranslations
>
export type HelpCenterHomeTranslation = Row<
	typeof schema.tables.helpCenterHomeTranslations
>
export type AiChatSettingsTranslation = Row<
	typeof schema.tables.aiChatSettingsTranslations
>
export type SlaPolicy = Row<typeof schema.tables.slaPolicies>
export type CodingAgentRun = Row<typeof schema.tables.codingAgentRuns>
export type ApiKey = Row<typeof schema.tables.apiKeys>
export type Webhook = Row<typeof schema.tables.webhooks>
export type WebhookDelivery = Row<typeof schema.tables.webhookDeliveries>

export const permissions = definePermissions<ZeroJwtPayload, Schema>(
	schema,
	() => {
		const isLoggedIn = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<keyof Schema["tables"], Schema>,
		) => eb.cmpLit(authData.userId, "!=", null)

		const allowWorkspaceAccess = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"workspaces", Schema>,
		) =>
			eb.and(
				eb.exists("memberships", (q) => {
					return q.where((eb) => {
						return eb.cmp("userId", "=", authData.userId)
					})
				}),
			)

		const allowWorkspaceWriteAccess = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"workspaces", Schema>,
		) =>
			eb.and(
				eb.cmp("id", "=", authData.workspaceId),
				eb.cmpLit(authData.role, "!=", "member"),
				eb.exists("memberships", (q) => {
					return q.where((eb) => {
						return eb.cmp("userId", "=", authData.userId)
					})
				}),
			)

		const allowUserWriteAccess = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"users", Schema>,
		) => eb.and(eb.cmp("id", "=", authData.userId))

		const canSeeUser = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"users", Schema>,
		) =>
			eb.exists("memberships", (q) => {
				return q.where((eb) => {
					return eb.exists("workspace", (q) => {
						return q.where((eb) => {
							return eb.exists("memberships", (q) => {
								return q.where((eb) => {
									return eb.cmp("userId", "=", authData.userId)
								})
							})
						})
					})
				})
			})

		const canAccessWorkspaceEntity = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<
				| "memberships"
				| "feedbacks"
				| "companies"
				| "contacts"
				| "contactCompanies"
				| "tagGroups"
				| "tags"
				| "videos"
				| "meetingRecordings"
				| "transcriptUtterances"
				| "projects"
				| "issues"
				| "changelogs"
				| "helpCenterGroups"
				| "helpCenterTabs"
				| "helpCenterArticles"
				| "helpCenterCustomHomeSections"
				| "widgetSettings"
				| "aiChatSettings"
				| "widgetLinks"
				| "sidebarSections"
				| "customViews"
				| "linearLabels"
				| "issueLabels"
				| "workspaceInvites"
				| "emailSettings"
				| "slackSettings"
				| "slackConnectSettings"
				| "slackChannels"
				| "ssoSettings"
				| "intercomSettings"
				| "hubspotSettings"
				| "plainSettings"
				| "threadMessages"
				| "threadActivities"
				| "emails"
				| "attachments"
				| "slackUsers"
				| "linearUsers"
				| "linearCycles"
				| "slackMessages"
				| "messageReactions"
				| "snippets"
				| "snippetFolders"
				| "allowedEmailDomains"
				| "feedbackTemplates"
				| "segments"
				| "upvotes"
				| "issueUpvotes"
				| "roadmapSettings"
				| "customerPortalSettings"
				| "linearRoadmaps"
				| "comments"
				| "aiChatSessions"
				| "liveChatSessions"
				| "issueTranslations"
				| "projectTranslations"
				| "changelogTranslations"
				| "helpCenterArticleTranslations"
				| "helpCenterGroupTranslations"
				| "helpCenterHomeTranslations"
				| "helpCenterArticleProposals"
				| "helpCenterArticleProposalTranslations"
				| "helpCenterProposalGroups"
				| "agentRecommendations"
				| "suggestions"
				| "aiChatSettingsTranslations"
				| "slaPolicies"
				| "chatMessages"
				| "teamsSettings"
				| "githubSettings"
				| "changelogTags"
				| "codingAgentRuns"
				| "apiKeys"
				| "webhooks"
				| "portalInstances",
				Schema
			>,
		) =>
			eb.and(
				eb.exists("workspace", (q) => {
					return q.where((eb) => {
						return eb.exists("memberships", (q) => {
							return q.where((eb) => {
								return eb.cmp("userId", "=", authData.userId)
							})
						})
					})
				}),
				eb.cmp("workspaceId", "=", authData.workspaceId),
			)

		// Customer-agent sessions (no userId) feed the inbox Agent tabs for the whole
		// team. An assistant chat carries its owner's title, so only they read it.
		const canReadAiChatSession = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"aiChatSessions", Schema>,
		) =>
			eb.and(
				canAccessWorkspaceEntity(authData, eb),
				eb.or(
					eb.cmp("userId", "IS", null),
					eb.cmp("userId", "=", authData.userId),
				),
			)

		// `webhookDeliveries` rows have no `workspaceId` of their own;
		// they're scoped via the parent `Webhook`. Walk the relation to
		// gate access on the same workspace check used everywhere else.
		const canAccessWebhookDelivery = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"webhookDeliveries", Schema>,
		) =>
			eb.exists("webhook", (q) =>
				q.where((eb) =>
					eb.and(
						eb.cmp("workspaceId", "=", authData.workspaceId),
						eb.exists("workspace", (q) =>
							q.where((eb) =>
								eb.exists("memberships", (q) =>
									q.where((eb) => eb.cmp("userId", "=", authData.userId)),
								),
							),
						),
					),
				),
			)

		const isOwnNotificationRow = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<
				"notifications" | "notificationPreferences",
				Schema
			>,
		) =>
			eb.and(
				eb.cmp("userId", "=", authData.userId),
				eb.cmp("workspaceId", "=", authData.workspaceId),
			)

		const isWorkspaceSubscription = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"notificationSubscriptions", Schema>,
		) => eb.cmp("workspaceId", "=", authData.workspaceId)

		// OAuth clients are only listed for the workspace that registered them
		// (ownerWorkspaceId). DCR clients leave that null and are never synced
		// to any client, so anonymous connect-flow apps stay invisible.
		const canAccessOwnedOAuthClient = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"oauthClients", Schema>,
		) =>
			eb.and(
				eb.cmp("ownerWorkspaceId", "=", authData.workspaceId),
				eb.exists("ownerWorkspace", (q) =>
					q.where((eb) =>
						eb.exists("memberships", (q) =>
							q.where((eb) => eb.cmp("userId", "=", authData.userId)),
						),
					),
				),
			)

		const canAccessBlockedSender = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"blockedSender", Schema>,
		) =>
			eb.exists("workspace", (q) => {
				return q.where((eb) => {
					return eb.and(
						eb.exists("memberships", (q) => {
							return q.where((eb) => {
								return eb.cmp("userId", "=", authData.userId)
							})
						}),
						eb.cmp("id", "=", authData.workspaceId),
					)
				})
			})

		const canAccessForwardedEmailIgnoreSender = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"forwardedEmailIgnoreSender", Schema>,
		) =>
			eb.exists("workspace", (q) => {
				return q.where((eb) => {
					return eb.and(
						eb.exists("memberships", (q) => {
							return q.where((eb) => {
								return eb.cmp("userId", "=", authData.userId)
							})
						}),
						eb.cmp("id", "=", authData.workspaceId),
					)
				})
			})

		const canAccessChangelogVersion = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"changelogVersions", Schema>,
		) =>
			eb.exists("changelog", (q) => {
				return q.where((eb) => {
					return eb.and(
						eb.exists("workspace", (q) => {
							return q.where((eb) => {
								return eb.exists("memberships", (q) => {
									return q.where((eb) => {
										return eb.cmp("userId", "=", authData.userId)
									})
								})
							})
						}),
						eb.cmp("workspaceId", "=", authData.workspaceId),
					)
				})
			})

		const canAccessHelpCenterArticleVersion = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"helpCenterArticleVersions", Schema>,
		) =>
			eb.exists("article", (q) => {
				return q.where((eb) => {
					return eb.and(
						eb.exists("workspace", (q) => {
							return q.where((eb) => {
								return eb.exists("memberships", (q) => {
									return q.where((eb) => {
										return eb.cmp("userId", "=", authData.userId)
									})
								})
							})
						}),
						eb.cmp("workspaceId", "=", authData.workspaceId),
					)
				})
			})

		const canAccessWorkspaces = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<
				| "memberships"
				| "feedbacks"
				| "companies"
				| "contacts"
				| "tagGroups"
				| "tags"
				| "videos"
				| "meetingRecordings"
				| "transcriptUtterances"
				| "projects"
				| "issues"
				| "changelogs"
				| "helpCenterGroups"
				| "helpCenterTabs"
				| "helpCenterArticles"
				| "helpCenterCustomHomeSections"
				| "widgetSettings"
				| "aiChatSettings"
				| "widgetLinks"
				| "sidebarSections"
				| "workspaceInvites"
				| "emailSettings"
				| "slackSettings"
				| "slackConnectSettings"
				| "slackChannels"
				| "ssoSettings"
				| "intercomSettings"
				| "hubspotSettings"
				| "plainSettings"
				| "threadMessages"
				| "threadActivities"
				| "emails"
				| "attachments"
				| "slackUsers"
				| "linearUsers"
				| "linearCycles"
				| "slackMessages"
				| "snippets"
				| "snippetFolders"
				| "allowedEmailDomains"
				| "feedbackTemplates"
				| "segments"
				| "upvotes"
				| "issueUpvotes"
				| "roadmapSettings"
				| "customerPortalSettings"
				| "linearRoadmaps"
				| "comments"
				| "aiChatSessions"
				| "liveChatSessions"
				| "codingAgentRuns",
				Schema
			>,
		) =>
			eb.and(
				eb.exists("workspace", (q) => {
					return q.where((eb) => {
						return eb.exists("memberships", (q) => {
							return q.where((eb) => {
								return eb.cmp("userId", "=", authData.userId)
							})
						})
					})
				}),
			)

		const canWriteUserFeedback = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"feedbacks", Schema>,
		) => eb.cmp("reporterId", "=", authData.userId)

		const canAccessLiveChatSession = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"liveChatMessages", Schema>,
		) =>
			eb.exists("session", (q) => {
				return q.where((eb) => eb.cmp("workspaceId", "=", authData.workspaceId))
			})

		const canSeeFeedback = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"feedbacks", Schema>,
		) =>
			eb.and(
				eb.or(
					eb.cmp("reporterId", "=", authData.userId),
					eb.cmp("assigneeId", "=", authData.userId),
					eb.cmpLit(authData.role, "!=", "member"),
				),
				canAccessWorkspaceEntity(authData, eb),
			)

		const canAccessCustomerNeed = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"customerNeeds", Schema>,
		) =>
			eb.exists("feedback", (q) => {
				return q.where((eb) => {
					return eb.and(
						eb.exists("workspace", (q) => {
							return q.where((eb) => {
								return eb.exists("memberships", (q) => {
									return q.where((eb) => {
										return eb.cmp("userId", "=", authData.userId)
									})
								})
							})
						}),
						eb.cmp("workspaceId", "=", authData.workspaceId),
					)
				})
			})

		const canAccessSidebarSectionHeader = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"sidebarSectionHeaders", Schema>,
		) =>
			eb.exists("section", (q) => {
				return q.where((eb) => {
					return eb.and(
						eb.exists("workspace", (q) => {
							return q.where((eb) => {
								return eb.exists("memberships", (q) => {
									return q.where((eb) => {
										return eb.cmp("userId", "=", authData.userId)
									})
								})
							})
						}),
						eb.cmp("workspaceId", "=", authData.workspaceId),
					)
				})
			})

		// A viewer must not be able to repoint the URL an admin's credentials
		// get posted to, or widen what the AI agent can reach.
		const canManageSidebarSection = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"sidebarSections", Schema>,
		) =>
			eb.and(
				canAccessWorkspaceEntity(authData, eb),
				eb.cmpLit(authData.role, "!=", "VIEWER"),
			)

		const canManageSidebarSectionHeader = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"sidebarSectionHeaders", Schema>,
		) =>
			eb.and(
				canAccessSidebarSectionHeader(authData, eb),
				eb.cmpLit(authData.role, "!=", "VIEWER"),
			)

		const canAccessWidgetContactAvatar = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"widgetContactAvatars", Schema>,
		) =>
			eb.exists("widgetSettings", (q) => {
				return q.where((eb) => {
					return eb.and(
						eb.exists("workspace", (q) => {
							return q.where((eb) => {
								return eb.exists("memberships", (q) => {
									return q.where((eb) => {
										return eb.cmp("userId", "=", authData.userId)
									})
								})
							})
						}),
						eb.cmp("workspaceId", "=", authData.workspaceId),
					)
				})
			})

		const canAccessPersonalSlackNotificationSettings = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"personalSlackNotificationSettings", Schema>,
		) =>
			eb.exists("membership", (q) => {
				return q.where((eb) => {
					return eb.and(
						eb.cmp("userId", "=", authData.userId),
						eb.cmp("workspaceId", "=", authData.workspaceId),
					)
				})
			})

		const canAccessCompanyToSegment = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"companyToSegments", Schema>,
		) =>
			eb.or(
				// Check access through company
				eb.exists("company", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
				// Check access through segment
				eb.exists("segment", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
			)

		const canAccessContactToSegment = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"contactToSegments", Schema>,
		) =>
			eb.or(
				// Check access through contact
				eb.exists("contact", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
				// Check access through segment
				eb.exists("segment", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
			)

		const canAccessFeedbackToTag = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"feedbackToTags", Schema>,
		) =>
			eb.or(
				// Check access through feedback
				eb.exists("feedback", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
				// Check access through tag
				eb.exists("tag", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
			)

		const canAccessChangelogToChangelogTag = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"changelogToChangelogTags", Schema>,
		) =>
			eb.exists("changelog", (q) => {
				return q.where((eb) => {
					return eb.and(
						eb.exists("workspace", (q) => {
							return q.where((eb) => {
								return eb.exists("memberships", (q) => {
									return q.where((eb) => {
										return eb.cmp("userId", "=", authData.userId)
									})
								})
							})
						}),
						eb.cmp("workspaceId", "=", authData.workspaceId),
					)
				})
			})

		const canAccessFeedbackToIssue = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"feedbackToIssues", Schema>,
		) =>
			eb.or(
				// Check access through feedback
				eb.exists("feedback", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
				// Check access through issue
				eb.exists("issue", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
			)

		const canAccessHelpCenterProposalGroupToIssue = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"helpCenterProposalGroupToIssues", Schema>,
		) =>
			eb.or(
				// Check access through proposal group
				eb.exists("group", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
				// Check access through issue
				eb.exists("issue", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
			)

		const canAccessFeedbackToProject = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"feedbackToProjects", Schema>,
		) =>
			eb.or(
				// Check access through feedback
				eb.exists("feedback", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
				// Check access through project
				eb.exists("project", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
			)

		const canAccessLinearRoadmapToProject = (
			authData: ZeroJwtPayload,
			eb: ExpressionBuilder<"linearRoadmapToProjects", Schema>,
		) =>
			eb.or(
				// Check access through linearRoadmap
				eb.exists("linearRoadmap", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
				// Check access through project
				eb.exists("project", (q) => {
					return q.where((eb) => {
						return eb.and(
							eb.exists("workspace", (q) => {
								return q.where((eb) => {
									return eb.exists("memberships", (q) => {
										return q.where((eb) => {
											return eb.cmp("userId", "=", authData.userId)
										})
									})
								})
							}),
							eb.cmp("workspaceId", "=", authData.workspaceId),
						)
					})
				}),
			)

		return {
			users: {
				row: {
					select: [canSeeUser],
					insert: NOBODY_CAN,
					update: {
						preMutation: [allowUserWriteAccess],
						postMutation: [allowUserWriteAccess],
					},
					delete: NOBODY_CAN,
				},
			},
			linearRoadmapToProjects: {
				row: {
					select: [canAccessLinearRoadmapToProject],
					insert: [canAccessLinearRoadmapToProject],
					update: {
						preMutation: [canAccessLinearRoadmapToProject],
						postMutation: [canAccessLinearRoadmapToProject],
					},
					delete: [canAccessLinearRoadmapToProject],
				},
			},
			workspaces: {
				row: {
					select: [allowWorkspaceAccess],
					insert: NOBODY_CAN,
					update: {
						preMutation: [allowWorkspaceWriteAccess],
						postMutation: [allowWorkspaceWriteAccess],
					},
					delete: NOBODY_CAN,
				},
			},
			memberships: {
				row: {
					select: [canAccessWorkspaces],
					insert: NOBODY_CAN,
					update: {
						preMutation: NOBODY_CAN,
						postMutation: NOBODY_CAN,
					},
					delete: NOBODY_CAN,
				},
			},
			feedbacks: {
				row: {
					select: [canSeeFeedback],
					insert: [canAccessWorkspaceEntity, canWriteUserFeedback],
					update: {
						preMutation: [canAccessWorkspaceEntity, canWriteUserFeedback],
						postMutation: [canAccessWorkspaceEntity, canWriteUserFeedback],
					},
					delete: [canAccessWorkspaceEntity, canWriteUserFeedback],
				},
			},
			companies: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			contacts: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			contactCompanies: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			tagGroups: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			tags: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			videos: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			meetingRecordings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			transcriptUtterances: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			projects: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			issues: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			customerNeeds: {
				row: {
					select: [canAccessCustomerNeed],
					insert: [canAccessCustomerNeed],
					update: {
						preMutation: [canAccessCustomerNeed],
						postMutation: [canAccessCustomerNeed],
					},
					delete: [canAccessCustomerNeed],
				},
			},
			changelogs: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			changelogTags: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			portalInstances: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			helpCenterGroups: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			helpCenterCustomHomeSections: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			helpCenterTabs: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			helpCenterArticles: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			widgetSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			widgetContactAvatars: {
				row: {
					select: [canAccessWidgetContactAvatar],
					insert: [canAccessWidgetContactAvatar],
					update: {
						preMutation: [canAccessWidgetContactAvatar],
						postMutation: [canAccessWidgetContactAvatar],
					},
					delete: [canAccessWidgetContactAvatar],
				},
			},
			aiChatSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			widgetLinks: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			sidebarSections: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canManageSidebarSection],
					update: {
						preMutation: [canManageSidebarSection],
						postMutation: [canManageSidebarSection],
					},
					delete: [canManageSidebarSection],
				},
			},
			linearLabels: {
				row: { select: [canAccessWorkspaceEntity] },
			},
			linearCycles: {
				row: { select: [canAccessWorkspaceEntity] },
			},
			issueLabels: {
				row: { select: [canAccessWorkspaceEntity] },
			},
			customViews: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			sidebarSectionHeaders: {
				row: {
					select: [canManageSidebarSectionHeader],
					insert: [canManageSidebarSectionHeader],
					update: {
						preMutation: [canManageSidebarSectionHeader],
						postMutation: [canManageSidebarSectionHeader],
					},
					delete: [canManageSidebarSectionHeader],
				},
			},
			workspaceInvites: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			emailSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			blockedSender: {
				row: {
					select: [canAccessBlockedSender],
					insert: [canAccessBlockedSender],
					update: {
						preMutation: [canAccessBlockedSender],
						postMutation: [canAccessBlockedSender],
					},
					delete: [canAccessBlockedSender],
				},
			},
			forwardedEmailIgnoreSender: {
				row: {
					select: [canAccessForwardedEmailIgnoreSender],
					insert: [canAccessForwardedEmailIgnoreSender],
					update: {
						preMutation: [canAccessForwardedEmailIgnoreSender],
						postMutation: [canAccessForwardedEmailIgnoreSender],
					},
					delete: [canAccessForwardedEmailIgnoreSender],
				},
			},
			personalSlackNotificationSettings: {
				row: {
					select: [canAccessPersonalSlackNotificationSettings],
					insert: [canAccessPersonalSlackNotificationSettings],
					update: {
						preMutation: [canAccessPersonalSlackNotificationSettings],
						postMutation: [canAccessPersonalSlackNotificationSettings],
					},
					delete: [canAccessPersonalSlackNotificationSettings],
				},
			},
			slackSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			slackConnectSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			// Rows come from Slack sync on the server; the client only edits their settings.
			slackChannels: {
				row: {
					select: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
				},
			},
			ssoSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			intercomSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			hubspotSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			plainSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			teamsSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			githubSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			threadMessages: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			emails: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			attachments: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			slackUsers: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			linearUsers: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			slackMessages: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			messageReactions: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			chatMessages: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			snippets: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			snippetFolders: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			allowedEmailDomains: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			feedbackTemplates: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			companyMetas: {
				row: {
					select: [isLoggedIn],
					insert: NOBODY_CAN,
					update: {
						preMutation: NOBODY_CAN,
						postMutation: NOBODY_CAN,
					},
					delete: NOBODY_CAN,
				},
			},
			segments: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			upvotes: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			issueUpvotes: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			roadmapSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			customerPortalSettings: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			linearRoadmaps: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			comments: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			// The server's AI chat handlers write these; the dashboard only archives
			// a user's own chats, and reads the rest for the Agent tabs.
			aiChatSessions: {
				row: {
					select: [canReadAiChatSession],
					insert: NOBODY_CAN,
					update: {
						preMutation: NOBODY_CAN,
						postMutation: NOBODY_CAN,
					},
					delete: NOBODY_CAN,
				},
			},
			liveChatSessions: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			liveChatMessages: {
				row: {
					select: [canAccessLiveChatSession],
					insert: [canAccessLiveChatSession],
					update: {
						preMutation: [canAccessLiveChatSession],
						postMutation: [canAccessLiveChatSession],
					},
					delete: [canAccessLiveChatSession],
				},
			},
			companyToSegments: {
				row: {
					select: [canAccessCompanyToSegment],
					insert: [canAccessCompanyToSegment],
					update: {
						preMutation: [canAccessCompanyToSegment],
						postMutation: [canAccessCompanyToSegment],
					},
					delete: [canAccessCompanyToSegment],
				},
			},
			contactToSegments: {
				row: {
					select: [canAccessContactToSegment],
					insert: [canAccessContactToSegment],
					update: {
						preMutation: [canAccessContactToSegment],
						postMutation: [canAccessContactToSegment],
					},
					delete: [canAccessContactToSegment],
				},
			},
			changelogToChangelogTags: {
				row: {
					select: [canAccessChangelogToChangelogTag],
					insert: [canAccessChangelogToChangelogTag],
					update: {
						preMutation: [canAccessChangelogToChangelogTag],
						postMutation: [canAccessChangelogToChangelogTag],
					},
					delete: [canAccessChangelogToChangelogTag],
				},
			},
			feedbackToTags: {
				row: {
					select: [canAccessFeedbackToTag],
					insert: [canAccessFeedbackToTag],
					update: {
						preMutation: [canAccessFeedbackToTag],
						postMutation: [canAccessFeedbackToTag],
					},
					delete: [canAccessFeedbackToTag],
				},
			},
			feedbackToIssues: {
				row: {
					select: [canAccessFeedbackToIssue],
					insert: [canAccessFeedbackToIssue],
					update: {
						preMutation: [canAccessFeedbackToIssue],
						postMutation: [canAccessFeedbackToIssue],
					},
					delete: [canAccessFeedbackToIssue],
				},
			},
			feedbackToProjects: {
				row: {
					select: [canAccessFeedbackToProject],
					insert: [canAccessFeedbackToProject],
					update: {
						preMutation: [canAccessFeedbackToProject],
						postMutation: [canAccessFeedbackToProject],
					},
					delete: [canAccessFeedbackToProject],
				},
			},
			changelogVersions: {
				row: {
					select: [canAccessChangelogVersion],
					insert: [canAccessChangelogVersion],
					update: {
						preMutation: [canAccessChangelogVersion],
						postMutation: [canAccessChangelogVersion],
					},
					delete: [canAccessChangelogVersion],
				},
			},
			helpCenterArticleVersions: {
				row: {
					select: [canAccessHelpCenterArticleVersion],
					insert: [canAccessHelpCenterArticleVersion],
					update: {
						preMutation: [canAccessHelpCenterArticleVersion],
						postMutation: [canAccessHelpCenterArticleVersion],
					},
					delete: [canAccessHelpCenterArticleVersion],
				},
			},
			helpCenterArticleProposals: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			helpCenterArticleProposalTranslations: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			helpCenterProposalGroups: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			// Rows come from the suggestion job; people only accept or dismiss them.
			suggestions: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: NOBODY_CAN,
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: NOBODY_CAN,
				},
			},
			// Rows come from the recommendations engine; people only close them.
			agentRecommendations: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: NOBODY_CAN,
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: NOBODY_CAN,
				},
			},
			helpCenterProposalGroupToIssues: {
				row: {
					select: [canAccessHelpCenterProposalGroupToIssue],
					insert: [canAccessHelpCenterProposalGroupToIssue],
					update: {
						preMutation: [canAccessHelpCenterProposalGroupToIssue],
						postMutation: [canAccessHelpCenterProposalGroupToIssue],
					},
					delete: [canAccessHelpCenterProposalGroupToIssue],
				},
			},
			issueTranslations: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			projectTranslations: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			changelogTranslations: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			helpCenterArticleTranslations: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			helpCenterGroupTranslations: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			helpCenterHomeTranslations: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			aiChatSettingsTranslations: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			slaPolicies: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: [canAccessWorkspaceEntity],
					update: {
						preMutation: [canAccessWorkspaceEntity],
						postMutation: [canAccessWorkspaceEntity],
					},
					delete: [canAccessWorkspaceEntity],
				},
			},
			threadActivities: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: NOBODY_CAN,
					update: {
						preMutation: NOBODY_CAN,
						postMutation: NOBODY_CAN,
					},
					delete: NOBODY_CAN,
				},
			},
			codingAgentRuns: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: NOBODY_CAN,
					update: {
						preMutation: NOBODY_CAN,
						postMutation: NOBODY_CAN,
					},
					delete: NOBODY_CAN,
				},
			},
			// Public API v2: Zero is read-only here. The settings UI lists
			// keys/webhooks via Zero for instant updates, but
			// create/update/revoke go through tRPC so the server can
			// generate secrets, hash them, and never round-trip plaintext
			// through the sync layer.
			apiKeys: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: NOBODY_CAN,
					update: {
						preMutation: NOBODY_CAN,
						postMutation: NOBODY_CAN,
					},
					delete: NOBODY_CAN,
				},
			},
			webhooks: {
				row: {
					select: [canAccessWorkspaceEntity],
					insert: NOBODY_CAN,
					update: {
						preMutation: NOBODY_CAN,
						postMutation: NOBODY_CAN,
					},
					delete: NOBODY_CAN,
				},
			},
			// Rows are written by the fan-out function; members only flip read/archive state via server mutators.
			notifications: {
				row: {
					select: [isOwnNotificationRow],
					insert: NOBODY_CAN,
					update: {
						preMutation: [isOwnNotificationRow],
						postMutation: [isOwnNotificationRow],
					},
					delete: NOBODY_CAN,
				},
			},
			notificationSubscriptions: {
				row: {
					select: [isWorkspaceSubscription],
					insert: NOBODY_CAN,
					update: {
						preMutation: NOBODY_CAN,
						postMutation: NOBODY_CAN,
					},
					delete: NOBODY_CAN,
				},
			},
			notificationPreferences: {
				row: {
					select: [isOwnNotificationRow],
					insert: NOBODY_CAN,
					update: {
						preMutation: NOBODY_CAN,
						postMutation: NOBODY_CAN,
					},
					delete: NOBODY_CAN,
				},
			},
			// Read-only audit log. All mutations live on the server in
			// the dispatch / deliver / retry Inngest functions.
			webhookDeliveries: {
				row: {
					select: [canAccessWebhookDelivery],
					insert: NOBODY_CAN,
					update: {
						preMutation: NOBODY_CAN,
						postMutation: NOBODY_CAN,
					},
					delete: NOBODY_CAN,
				},
			},
			// Read-only: registration/consent/revocation run server-side via
			// the /oauth endpoints; secret hashes never round-trip a client mutation.
			oauthClients: {
				row: {
					select: [canAccessOwnedOAuthClient],
					insert: NOBODY_CAN,
					update: {
						preMutation: NOBODY_CAN,
						postMutation: NOBODY_CAN,
					},
					delete: NOBODY_CAN,
				},
			},
		} satisfies PermissionsConfig<ZeroJwtPayload, Schema>
	},
)

// Expose the builder globally for easy debugging in the console
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
;(globalThis as any).__zql = createBuilder(schema)
